<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import SubscriptionStepper from './SubscriptionStepper.vue'
import DateField from './DateField.vue'
import { DEFAULT_LOCALE, type Locale } from '@/i18n'
import { subscriptionHref, answerSlug, stepOptions, type ProductKey } from '@/router/products'
import QuestionStep from '@/components/ui/subscription/QuestionStep.vue'
import ResultsStep, { type ResultCard, type ResultDetail } from '@/components/ui/subscription/ResultsStep.vue'
import TopUpStep from '@/components/ui/subscription/TopUpStep.vue'
import CrossSellStep, { type CrossSellCard } from '@/components/ui/subscription/CrossSellStep.vue'
import InfoForm from '@/components/ui/subscription/InfoForm.vue'
import { useSubscriptionState } from '@/composables/useSubscriptionState'
import { useSubscriptionFlow } from '@/composables/useSubscriptionFlow'
import { useSubscriptionOffer } from '@/composables/useSubscriptionOffer'
import { useSubscription } from '@/composables/useSubscription'
import {
  COUNTRY_CODES_DESTINATION_EUROPE,
  COUNTRY_CODES_DESTINATION_WORLD,
  flagEmoji,
  countryLabel,
} from '@/data/countries'
import { NATIONALITIES } from '@/data/nationalities'
import { guaranteeSheetUrlFor } from '@/data/guaranteeSheets'

const props = defineProps<{
  productKey?: ProductKey
  productLabel: string
}>()

const { t } = useI18n()
const router = useRouter()
const { locale, answerPathSlugs, answers, currentStepKey, currentStepIndex, totalSteps } = useSubscriptionState()

// Offre retenue + récap de finalisation : view-models partagés avec InfoForm
// (offre choisie, dates de contrat, sous-étapes infos, totaux, libellés). Les
// drapeaux produit et helpers d'affichage viennent aussi de là (source unique).
const {
  isHealthExpenses,
  isCivil,
  isAbroad,
  isArrivingFrance,
  isHealthTopUp,
  isRegionalAura,
  isSolidariteExam,
  ageBounds,
  formatPrice,
  formatDate,
  detailName,
  infoStepCount,
  topUpName,
  endDate,
  civilEndDate,
  franceEndDate,
  abroadChosen,
  civilChosen,
  franceChosen,
  yearlyTotal,
  stayDurationText,
  topUpBundleName,
  regionalOfferName,
  regionalOfferImage,
  examOfferName,
  summaryCards,
  productImage,
  topUpImage,
} = useSubscriptionOffer(() => props.productKey)

/** Bounds interpolated into the "age" step's option labels. Reads the very same
 *  `ageBounds` the birth-date guards use, so the bracket advertised to the user is
 *  exactly the one their date of birth will later be checked against. */
const ageLabelParams = computed(() => ageBounds.value)

/** Produits dont l'arbre exclut non-étudiants, mineurs et trop âgés (RC, étranger,
 *  arrivée en France). La santé fait sortir les mineurs et les +30 ans. */
const hasFullExits = computed(() => isCivil.value || isAbroad.value || isArrivingFrance.value)

/** Écran de sortie (popup « OUPS » inline).
 *  - santé : mineur (bucket "young" = self < 18 / child < 15) ou +30 ans (senior).
 *  - renforcer-sante : mineur uniquement.
 *  - Solidarité exam : mineur toujours ; en plus, un non-étudiant n'est autorisé
 *    que sur la tranche 18-30 ("mid") → un non-étudiant "senior" (+30) sort aussi.
 *  - RC / étranger : non-étudiant, mineur (young) ou trop âgé (senior) → seuls
 *    les étudiants de la tranche "mid" poursuivent. */
const showExit = computed(() => {
  const a = answers.value
  if (isHealthExpenses.value) return a.age === 'young' || a.age === 'senior'
  if (isHealthTopUp.value) return a.age === 'young'
  if (isSolidariteExam.value) return a.age === 'young' || (a.isStudent === 'no' && a.age === 'senior')
  if (isRegionalAura.value) return a.age === 'young' || a.age === 'senior' || a.residency === 'no'
  if (hasFullExits.value) return a.isStudent === 'no' || a.age === 'young' || a.age === 'senior'
  return false
})

/** Raison de la sortie, pour choisir le message (priorité : résidence > étudiant > âge).
 *  Pour Solidarité exam, être non-étudiant n'est pas en soi une sortie (les 18-30
 *  non-étudiants poursuivent) : la raison vient alors de l'âge. */
const exitReason = computed<'notStudent' | 'minor' | 'senior' | 'nonResident'>(() => {
  const a = answers.value
  if (isRegionalAura.value && a.residency === 'no') return 'nonResident'
  if (a.isStudent === 'no' && !isSolidariteExam.value) return 'notStudent'
  if (a.age === 'senior') return 'senior'
  return 'minor'
})

/** Message de l'écran de sortie selon le produit, la raison et le public.
 *  Santé : messages historiques (mineur / +30). AURA : messages dédiés (résidence,
 *  +30, mineur). Autres produits (RC, étranger, France) : messages génériques. */
const exitMessage = computed(() => {
  const a = answers.value
  const audience = a.forWhom === 'child' ? 'Child' : 'Self'
  // Health: minor message (historical) or senior (+30) message (SI-1398).
  if (isHealthExpenses.value) return t(`subscription.exit.${a.age === 'senior' ? 'senior' : 'minor'}${audience}`)
  if (isHealthTopUp.value) return t(`subscription.exit.minor${audience}`)
  if (isRegionalAura.value) return t(`subscription.exit.aura.${exitReason.value}${audience}`)
  // Étranger (packs PME) : messages dédiés — la limite d'âge y est 34 ans (contre
  // 30 ailleurs), d'où un « senior » propre au lieu du message générique.
  if (isAbroad.value) return t(`subscription.exit.abroad.${exitReason.value}${audience}`)
  // Solidarité exam + produits à sorties complètes : message générique par raison
  // (mineur / trop âgé), qui couvre le +30 non-étudiant nouvellement exclu.
  return t(`subscription.exit.generic.${exitReason.value}${audience}`)
})

/** Écran de résultats = étape de sélection de la formule (depenses-sante).
 *  On sort de l'arbre de décision pour présenter les produits recommandés ;
 *  choisir une formule renseigne l'étape "plan" et avance le parcours. */
const showResults = computed(
  () => isHealthExpenses.value && currentStepKey.value === 'plan',
)

/** Écran surcomplémentaire « Tonic-Études » : proposé après une formule
 *  essentielle/minimale, juste avant le cross-sell (depenses-sante). */
const showTopUp = computed(
  () => isHealthExpenses.value && currentStepKey.value === 'topUp',
)
const topUpDetailOpen = ref(false)

/** Détail « En savoir + » de la surcomplémentaire (contenu local fr/en). */
const topUpDetailContent: Record<Locale, PlanDetail> = {
  fr: {
    summary: 'La surcomplémentaire étudiante qui rembourse ta vie étudiante (taxi, fruits et légumes, ostéo…), et renforce ta complémentaire santé actuelle.',
    rows: [
      { label: 'Forfait fruits et légumes', value: '40€/an' },
      { label: 'Taxi soirée (entre 22h et 6h)', value: '15€/an' },
      { label: 'Pilule contraceptive', value: '60€/an' },
      { label: 'Médicaments hors prescription', value: '20€/an' },
      { label: 'Forfait test de grossesse', value: '10€/an' },
      { label: 'Médecine douce', value: '50€/an' },
      { label: 'Prévention : préservatifs', value: '20€/an' },
      { label: 'Protections périodiques', value: '30€/an' },
      { label: 'Optique : lentilles non remboursables', value: '50€/an' },
      { label: 'Vaccins non remboursés par la Sécurité sociale', value: '60€/an' },
    ],
  },
  en: {
    summary: 'The student top-up cover that reimburses your student life (taxi, fruit & vegetables, osteopathy…) and boosts your current health cover.',
    rows: [
      { label: 'Fruit & vegetable allowance', value: '€40/year' },
      { label: 'Evening taxi (10pm–6am)', value: '€15/year' },
      { label: 'Contraceptive pill', value: '€60/year' },
      { label: 'Over-the-counter medication', value: '€20/year' },
      { label: 'Pregnancy test allowance', value: '€10/year' },
      { label: 'Alternative medicine', value: '€50/year' },
      { label: 'Prevention: condoms', value: '€20/year' },
      { label: 'Period products', value: '€30/year' },
      { label: 'Optical: non-reimbursable lenses', value: '€50/year' },
      { label: 'Vaccines not reimbursed by Social Security', value: '€60/year' },
    ],
  },
}
// The top-up screen renders a single hard-coded formula, so its slug is known
// statically — unlike the results screens, which read it from the backend bundle.
const TOP_UP_SLUG = 'tonic'
const topUpDetail = computed<ResultDetail>(() => ({
  ...(topUpDetailContent[locale.value] ?? topUpDetailContent.fr),
  sheetUrl: guaranteeSheetUrlFor(TOP_UP_SLUG),
}))

/** Écran cross-sell : une fois la formule choisie, on propose une assurance
 *  complémentaire (santé, renforcer-sante, mutuelle AURA). 2 add-ons ou « non merci ». */
const showCrossSell = computed(
  () => (isHealthExpenses.value || isHealthTopUp.value || isRegionalAura.value) && currentStepKey.value === 'crossSell',
)

/** Add-ons proposés, prix €/an — dérivés du back-end (différence upsell − base),
 *  voir useSubscriptionFlow ; un add-on sans upsell correspondant est masqué.
 *  Commun à la santé, à la surcomplémentaire et à la mutuelle AURA (mêmes add-ons
 *  AVE Sécurité / Indispensable). */
const crossSellAddons = computed(() => healthCrossSellAddons.value)

/** Garanties par add-on — contenu local fr/en (même approche que planDetails). */
const crossSellFeatures: Record<string, Record<Locale, string[]>> = {
  security: {
    fr: [
      'Responsabilité civile',
      'Assurance accident corporel',
      'Stages, limité à 6 mois',
      'Garantie défense et recours en France et en U.E.',
      "Prestations d'assistance",
      "Risques Internet (litiges suite à achat d'un bien pour une consommation privée, usurpation d'identité et e-réputation)",
    ],
    en: [
      'Personal liability',
      'Personal accident insurance',
      'Internships, limited to 6 months',
      'Legal defence and recourse in France and the E.U.',
      'Assistance services',
      'Internet risks (disputes following the purchase of goods for private use, identity theft and e-reputation)',
    ],
  },
  indispensable: {
    fr: [
      'Responsabilité civile',
      'Assurance accident corporel',
      'Stages, limité à 6 mois',
      'Garantie défense et recours en France et en U.E.',
      "Prestations d'assistance",
    ],
    en: [
      'Personal liability',
      'Personal accident insurance',
      'Internships, limited to 6 months',
      'Legal defence and recourse in France and the E.U.',
      'Assistance services',
    ],
  },
}
function featuresFor(key: string): string[] {
  return crossSellFeatures[key]?.[locale.value] ?? crossSellFeatures[key]?.fr ?? []
}

/** Cross-sell screen view models for the generic CrossSellStep.
 *  Both health / health-top-up share the two-part "offer + saving" notice style;
 *  each product supplies its own lead/strong copy. */
const crossSellNotice = computed(() => {
  if (isHealthExpenses.value) {
    return {
      lead: t('subscription.crossSell.healthHighlightLead'),
      strong: t('subscription.crossSell.healthHighlightStrong'),
    }
  }
  if (isHealthTopUp.value) {
    return {
      lead: t('subscription.crossSell.topUpHighlightLead'),
      strong: t('subscription.crossSell.topUpHighlightStrong'),
    }
  }
  return null
})
const crossSellCards = computed<CrossSellCard[]>(() =>
  crossSellAddons.value.map((a) => ({
    key: a.key,
    name: a.labelKey ? t(`bundles.${a.labelKey}`) : t(`subscription.crossSell.addons.${a.key}.name`),
    priceYear: a.pricePerYear,
    features: featuresFor(a.key),
  })),
)

/** Formules en colonnes : la liste, les tarifs et la recommandation viennent
 *  du back-end (`available_bundles`), pas d'une table en dur. Chaque bundle est
 *  rattaché à son slug de parcours (`tierKey`) pour conserver libellés i18n,
 *  détails et slugs d'URL. Voir useSubscriptionFlow. */
const {
  products: recommendedProducts,
  crossSellAddons: healthCrossSellAddons,
  tonicPrice: healthTonicPrice,
  loading: bundlesLoading,
  error: bundlesError,
  reload: reloadBundles,
} = useSubscriptionFlow()

/* ----------------------------------------------------------------
 * Écran de résultats « responsabilité civile » (Assurance Vie Étudiante).
 * Présenté une fois la question internet répondue (fin de l'arbre RC).
 * La recommandation (badge) suit la réponse internet : oui → Sécurité (risques
 * numériques inclus), non → Indispensable. La sélection finale sera gérée
 * back-end ; on présente ici la liste + le détail « En savoir + ».
 * ----------------------------------------------------------------*/
const showCivilResults = computed(
  () => isCivil.value && currentStepKey.value === 'plan',
)

/** Illustrations dédiées (à déposer dans /public/products/, comme la santé). */
const civilImages: Record<string, string> = {
  security: '/products/assurance-vie-etudiante-securite.webp',
  indispensable: '/products/assurance-vie-etudiante-indispensable.webp',
}

/** Écran « Votre offre sur-mesure » (RC) : date de début + date de fin + tacite.
 *  Présenté une fois l'offre choisie (fin de l'arbre RC). */
const showCivilSummary = computed(
  () => isCivil.value && currentStepKey.value === null && civilChosen.value !== null,
)

/* ----------------------------------------------------------------
 * Écran de résultats « étranger » : un seul pack recommandé selon la
 * destination (Europe → Pack Europe ; hors Europe → Pack Monde). Tarif mensuel.
 * ----------------------------------------------------------------*/
const showAbroadResults = computed(
  () => isAbroad.value && currentStepKey.value === 'plan',
)

/** Illustrations packs étranger (à déposer dans /public/products/). */
const abroadImages: Record<string, string> = {
  packEurope: '/products/pack-europe.webp',
  packMonde: '/products/pack-monde.webp',
}

/** Pack étranger retenu : la formule choisie (étape « plan »), avec repli sur la
 *  destination tant qu'aucun pack n'est sélectionné. Sert à choisir la liste des
 *  pays de destination (Europe vs Monde) sur l'écran « Votre offre ». */
const abroadPack = computed(() =>
  answers.value.plan || (answers.value.destination === 'outsideEurope' ? 'packMonde' : 'packEurope'),
)

/* ----------------------------------------------------------------
 * Écran de résultats « j'arrive en France » : un seul pack recommandé selon la
 * durée du séjour (≤ 3 mois → Global ; 3-8 mois → Confort). > 8 mois bascule
 * vers la complémentaire santé (cf. selectFranceStay).
 * ----------------------------------------------------------------*/
const showFranceResults = computed(
  () => isArrivingFrance.value && currentStepKey.value === 'plan',
)

/** Illustrations packs « en France » (à déposer dans /public/products/). */
const franceImages: Record<string, string> = {
  packFranceGlobal: '/products/pack-en-france-global.webp',
  packFranceConfort: '/products/pack-en-france-confort.webp',
}

/** Pack recommandé selon la durée : court (≤ 3 mois) → Global ; sinon Confort.
 *  Utilisé pour les paliers de durée du formulaire (franceDurations). */
const francePack = computed(() =>
  answers.value.franceStay === 'short' ? 'packFranceGlobal' : 'packFranceConfort',
)

/** « > 8 mois » → bascule vers la complémentaire santé (même forWhom/étudiant/âge,
 *  on arrive sur la question consultations). Sinon, choix classique. */
function selectFranceStay(value: string) {
  if (value === 'long') {
    router.push(subscriptionHref('healthExpenses', answerPathSlugs.value, locale.value))
    return
  }
  selectChoice(value)
}

/** Écran de résultats « je renforce ma santé » : Tonic-Études (surcomplémentaire).
 *  Produit unique recommandé ; détail/image réutilisés de la santé. */
const showHealthTopUpResults = computed(
  () => isHealthTopUp.value && currentStepKey.value === 'plan',
)

/* ----------------------------------------------------------------
 * Écran de résultats « mutuelle Auvergne-Rhône-Alpes » : 2 formules
 * (Primordiale 20€/mois, Renforcée 32€/mois), Primordiale recommandée.
 * ----------------------------------------------------------------*/
const showRegionalResults = computed(
  () => isRegionalAura.value && currentStepKey.value === 'plan',
)

/* ----------------------------------------------------------------
 * Écran de résultats « Solidarité Exam » : bundle unique (Solidarité exam),
 * toujours recommandé côté back-end (available_bundles). Couverture annuelle.
 * ----------------------------------------------------------------*/
const showSolidariteExamResults = computed(
  () => isSolidariteExam.value && currentStepKey.value === 'plan',
)
/** Illustration Solidarité Exam (déposée dans /public/products/). */
const examImage = '/products/solidarite-exam.webp'

/** Détail « En savoir + » de Solidarité Exam (description générique, contenu
 *  local fr/en). Le bundle est unique, un seul contenu suffit. */
const solidariteExamDetailContent: Record<Locale, PlanDetail> = {
  fr: {
    summary: 'La protection en cas de coup dur. Un capital pour rebondir en cas de redoublement.',
    rows: [
      {
        label: "Allocation d'étude en cas de non-présentation à un examen afin de couvrir les dépenses de la nouvelle année d'études",
        value: '5 000 €',
      },
      {
        label: "L'aide est versée en cas d'hospitalisation personnelle ou de décès de l'un des parents et si cela entraîne le redoublement et que l'étudiant repasse ses examens",
        value: '',
      },
    ],
  },
  en: {
    summary: 'Protection when things go wrong. A lump sum to bounce back if you have to repeat a year.',
    rows: [
      {
        label: 'Study allowance if you cannot sit an exam, to cover the costs of the new academic year',
        value: '€5,000',
      },
      {
        label: 'Paid in the event of your own hospitalisation or the death of a parent, where it leads to repeating the year and the student re-sits their exams',
        value: '',
      },
    ],
  },
}
const solidariteExamDetail = computed(
  () => solidariteExamDetailContent[locale.value] ?? solidariteExamDetailContent.fr,
)
/** Illustrations mutuelle AURA (à déposer dans /public/products/). */
const regionalImages: Record<string, string> = {
  primordiale: '/products/aura-primordiale.webp',
  renforcee: '/products/aura-renforcee.webp',
}
/** Options optique/dentaire réduites pour AURA (2 choix au lieu de 3). */
const opticalOptions = computed(() =>
  isRegionalAura.value ? (['high', 'low'] as const) : (['high', 'low', 'none'] as const),
)
const dentalOptions = computed(() =>
  isRegionalAura.value ? (['yes', 'no'] as const) : (['yes', 'yesProsthesis', 'no'] as const),
)

/* ----------------------------------------------------------------
 * Écran récapitulatif + formulaire (parcours terminé · depenses-sante).
 * ----------------------------------------------------------------*/
const showSummary = computed(
  () => isHealthExpenses.value && currentStepKey.value === null,
)

/** Stepper : la phase de finalisation du parcours santé compte pour 6 étapes
 *  (1 récap + formulaire date/durée, 4 sous-étapes « Vos informations »,
 *  1 récap final avec CTA Payer). Récap-form = N+1 ; infos 1..4 = N+2..N+5 ;
 *  récap final = N+6 (infoStep = 5). */
const stepperTotal = computed(
  // Finalisation = offre/récap (1) + sous-étapes infos + récap final (1).
  () => totalSteps.value + infoStepCount.value + 2,
)
const stepperCurrent = computed(
  () => infoActive.value ? currentStepIndex.value + infoStep.value : currentStepIndex.value,
)

/* --- Formulaire : date de début (date picker) + durée ---
 * startDate/duration partagés via useSubscription (alimente « Votre tarif »). */
const DURATIONS = [12, 9, 6] as const
const { startDate, stayEnd, destinationCountry, nationality, duration } = useSubscription()

/** Tarif annuel Tonic-Études affiché dans le parcours santé (back-end) ; null
 *  tant que le bundle n'est pas chargé (le prix est alors masqué). */
const healthTonicPerYear = computed(() => healthTonicPrice.value?.perYear ?? null)
const submitted = ref(false)
const startTouched = ref(false)

/** Date minimale sélectionnable : demain (ni passé, ni le jour même). */
const minStartDate = computed(() => {
  const now = new Date()
  return new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1)
})

/* --- Offre « étranger » : plage de séjour (date range) + pays de destination --- */
/** Date de début la plus tardive autorisée : demain + 6 mois.
 *  Règle métier commune à tous les produits proposant une date de début. */
const startMaxDate = computed(() => {
  const now = new Date()
  return new Date(now.getFullYear(), now.getMonth() + 6, now.getDate() + 1)
})
/** Borne max formatée (jj/mm/aaaa) pour les libellés d'erreur de date. */
const startMaxLabel = computed(() => formatDate(startMaxDate.value))

/** Pays de destination retenu (parcours étranger). Proxy vers l'état partagé
 *  persisté (destinationCountry) ; pont '' ↔ null pour l'option placeholder. */
const abroadCountry = computed({
  get: () => destinationCountry.value ?? '',
  set: (v) => { destinationCountry.value = v || null },
})

/** Liste pays selon le pack : Europe → liste UE/EEE ; Monde → liste mondiale. */
const destinationCountryOptions = computed(() => {
  const codes = abroadPack.value === 'packEurope'
    ? COUNTRY_CODES_DESTINATION_EUROPE
    : COUNTRY_CODES_DESTINATION_WORLD
  return codes
    .map((code) => ({ code, name: countryLabel(code, locale.value), flag: flagEmoji(code) }))
    .sort((a, b) => a.name.localeCompare(b.name, locale.value))
})

/** Écran « Votre offre sur-mesure » (étranger) : plage de séjour + pays. */
const showAbroadSummary = computed(
  () => isAbroad.value && currentStepKey.value === null && abroadChosen.value !== null,
)

/** Bounds of the end-of-stay date: the start date itself … start + 12 months.
 *
 *  SI-1452 — the calendar used to open at start + 14 days, mirroring a 15-day minimum the
 *  backend enforced. That minimum is gone: a stay is whatever the member booked, down to a
 *  single day ("du 15 août au 15 août"), and anything up to 15 days is billed at the
 *  half-month rate (see `pmeBilledMonths`). The lower bound is therefore the start date
 *  itself — both bounds are inclusive, so picking the same day means one day of cover.
 *
 *  The upper bound stays: these packs do not renew, so the stay cannot outlive its term. */
const stayEndMin = computed<Date | null>(() => startDate.value)
const stayEndMax = computed<Date | null>(() => {
  if (!startDate.value) return null
  const s = startDate.value
  return new Date(s.getFullYear(), s.getMonth() + 12, s.getDate())
})

// Si le début change et rend la fin invalide (hors bornes), on réinitialise la fin.
watch(startDate, () => {
  if (!isAbroad.value || !stayEnd.value) return
  const e = stayEnd.value.getTime()
  if ((stayEndMin.value && e < stayEndMin.value.getTime()) ||
      (stayEndMax.value && e > stayEndMax.value.getTime())) {
    stayEnd.value = null
  }
})

const startError = computed(() => (submitted.value || startTouched.value) && !startDate.value)
const durationError = computed(() => submitted.value && !duration.value)
const stayEndError = computed(() => submitted.value && !stayEnd.value)
const abroadCountryError = computed(() => submitted.value && !abroadCountry.value)

/* --- Offre « j'arrive en France » : date de début + durée (paliers) + nationalité --- */
/** Durées proposées selon le pack : Global (≤ 3 mois) → 1-3 ; Confort → 4-8. */
const franceDurations = computed<number[]>(() =>
  francePack.value === 'packFranceGlobal' ? [1, 2, 3] : [4, 5, 6, 7, 8],
)

/** Nationalité (parcours « en France ») : v-model du select, pont '' ↔ null sur
 *  l'état partagé persisté (nationality) — comme abroadCountry pour l'étranger.
 *  La valeur stockée/envoyée est le code pays ISO. */
const nationalitySelect = computed({
  get: () => nationality.value ?? '',
  set: (v) => { nationality.value = v || null },
})

/** Options de nationalité : adjectif FR affiché, code pays ISO en valeur.
 *  Ordre métier conservé (cf. data/nationalities). */
const nationalities = NATIONALITIES

/** Écran « Votre offre sur-mesure » (en France) : date début + durée + nationalité. */
const showFranceSummary = computed(
  () => isArrivingFrance.value && currentStepKey.value === null && franceChosen.value !== null,
)

const franceDurationError = computed(
  () => submitted.value && !(duration.value && franceDurations.value.includes(duration.value)),
)
const nationalityError = computed(() => submitted.value && !nationality.value)

/* --- Offre « je renforce ma santé » : Tonic (+ assurance), durée fixe 1 an --- */
/** Écran « Votre offre sur-mesure » (Tonic-Études), une fois le cross-sell répondu. */
const showHealthTopUpSummary = computed(
  () => isHealthTopUp.value && currentStepKey.value === null && answers.value.plan === 'tonic',
)

/* --- Offre « mutuelle Auvergne-Rhône-Alpes » : formule (+ assurance), durée 1 an --- */
const showRegionalSummary = computed(
  () =>
    isRegionalAura.value &&
    currentStepKey.value === null &&
    (answers.value.plan === 'primordiale' || answers.value.plan === 'renforcee'),
)

/* --- Offre « Solidarité Exam » : bundle unique, durée fixe 1 an --- */
const showSolidariteExamSummary = computed(
  () => isSolidariteExam.value && currentStepKey.value === null && answers.value.plan === 'exam',
)

function submitForm() {
  submitted.value = true
  if (isAbroad.value) {
    // Étranger : plage de séjour complète + pays de destination requis.
    if (!startDate.value || !stayEnd.value || !abroadCountry.value) return
  } else if (isArrivingFrance.value) {
    // En France : date de début + durée valide (paliers) + nationalité.
    if (!startDate.value || !duration.value || !franceDurations.value.includes(duration.value) || !nationality.value) return
  } else {
    if (!startDate.value) return
    // Santé : une durée doit aussi être choisie. RC / renforcer-santé / AURA /
    // Solidarité Exam : durée fixe.
    if (!isCivil.value && !isHealthTopUp.value && !isRegionalAura.value && !isSolidariteExam.value && !duration.value) return
  }
  // Récap validé → on entre dans la phase « Vos informations » (déléguée à InfoForm).
  infoFormRef.value?.open()
}

/* Phase « Vos informations » : déléguée au composant InfoForm (smart,
 * self-contained — sous-étapes identité/adresse/contact/RIB + récap + paiement).
 * Le parent ne garde que l'état d'activation et la sous-étape courante (pour le
 * stepper), pilotés en v-model ; l'ouverture passe par la méthode exposée open(). */
const infoActive = ref(false)
const infoStep = ref(1)
const infoFormRef = ref<InstanceType<typeof InfoForm> | null>(null)

/** Contenu du « En savoir + » par formule (résumé + tableau récap 2 colonnes).
 *  Renseigné formule par formule ; les formules sans détail retombent sur un
 *  texte générique. */
type PlanDetail = { summary: string; rows: { label: string; value: string }[] }
const planDetails: Partial<Record<string, Record<string, PlanDetail>>> = {
  minimal: {
    fr: {
      summary: "La solution santé économique, couvrant les frais d'hospitalisation.",
      rows: [
        { label: 'Médecin et pharmacie', value: '—' },
        { label: 'Optique (lunettes, lentilles)', value: '—' },
        { label: 'Dentaire (soins, couronnes)', value: '—' },
        { label: 'Hospitalisation', value: 'Couverture de base' },
        { label: 'Médecines douces', value: '—' },
      ],
    },
    en: {
      summary: 'The budget-friendly health solution, covering hospitalisation costs.',
      rows: [
        { label: 'Doctor & pharmacy', value: '—' },
        { label: 'Optical (glasses, lenses)', value: '—' },
        { label: 'Dental (care, crowns)', value: '—' },
        { label: 'Hospitalisation', value: 'Basic cover' },
        { label: 'Alternative medicine', value: '—' },
      ],
    },
  },
  essential: {
    fr: {
      summary: 'La couverture santé incluant les remboursements de santé de base + tes rdv chez tes spécialistes.',
      rows: [
        { label: 'Médecin et pharmacie', value: 'Couverture de base (hors Psy et honoraires paramédicaux : masseurs-kiné, pédicures, podologues, infirmières...)' },
        { label: 'Optique (lunettes, lentilles)', value: 'Couverture de base' },
        { label: 'Dentaire (soins, couronnes)', value: '—' },
        { label: 'Hospitalisation', value: 'Couverture de base' },
        { label: 'Médecines douces', value: '—' },
      ],
    },
    en: {
      summary: 'Health cover including basic health reimbursements + your specialist appointments.',
      rows: [
        { label: 'Doctor & pharmacy', value: 'Basic cover (excluding psychiatry and paramedical fees: physiotherapists, chiropodists, podiatrists, nurses...)' },
        { label: 'Optical (glasses, lenses)', value: 'Basic cover' },
        { label: 'Dental (care, crowns)', value: '—' },
        { label: 'Hospitalisation', value: 'Basic cover' },
        { label: 'Alternative medicine', value: '—' },
      ],
    },
  },
  confort: {
    fr: {
      summary: "Le confort d'une couverture santé incluant les soins dentaires et optiques.",
      rows: [
        { label: 'Médecin et pharmacie', value: 'Couverture de base' },
        { label: 'Optique (lunettes)', value: 'De 50 à 200€ tous les deux ans' },
        { label: 'Dentaire (soins, couronnes)', value: 'Couverture de base' },
        { label: 'Hospitalisation', value: 'Couverture de base' },
        { label: 'Forfait fruits et légumes', value: '30€/an' },
        { label: 'Taxi soirée (22h–6h)', value: '15€/an' },
        { label: 'Pilule contraceptive', value: '40€/an' },
        { label: 'Médicaments hors prescription', value: '20€/an' },
        { label: 'Forfait test de grossesse', value: '10€/an' },
        { label: 'Médecine douce', value: '30€/an' },
        { label: 'Prévention : préservatifs', value: '20€/an' },
        { label: 'Prévention : protections périodiques', value: '30€/an' },
        { label: 'Vaccins non remboursés par la Sécurité sociale', value: '60€/an' },
        { label: 'Consultation psychologue', value: '40€/an' },
      ],
    },
    en: {
      summary: 'The comfort of health cover including dental and optical care.',
      rows: [
        { label: 'Doctor & pharmacy', value: 'Basic cover' },
        { label: 'Optical (glasses)', value: 'From €50 to €200 every two years' },
        { label: 'Dental (care, crowns)', value: 'Basic cover' },
        { label: 'Hospitalisation', value: 'Basic cover' },
        { label: 'Fruit & vegetable allowance', value: '€30/year' },
        { label: 'Evening taxi (10pm–6am)', value: '€15/year' },
        { label: 'Contraceptive pill', value: '€40/year' },
        { label: 'Over-the-counter medication', value: '€20/year' },
        { label: 'Pregnancy test allowance', value: '€10/year' },
        { label: 'Alternative medicine', value: '€30/year' },
        { label: 'Prevention: condoms', value: '€20/year' },
        { label: 'Prevention: period products', value: '€30/year' },
        { label: 'Vaccines not reimbursed by Social Security', value: '€60/year' },
        { label: 'Psychologist consultation', value: '€40/year' },
      ],
    },
  },
  allRisk: {
    fr: {
      summary: "Prise en charge de tes dépenses en optique, dentaire, auditif + tes rdv chez tes spécialistes même avec dépassement d'honoraire + des forfaits dédiés aux étudiants !",
      rows: [
        { label: 'Médecin et pharmacie', value: 'Couverture maximale' },
        { label: 'Optique (lunettes)', value: 'De 120 à 180€ tous les deux ans' },
        { label: 'Dentaire (soins, couronnes)', value: 'Couverture maximale' },
        { label: 'Hospitalisation', value: 'Couverture maximale + 15€ chambre particulière pendant 30 jours' },
        { label: 'Forfait fruits et légumes', value: '40€/an' },
        { label: 'Taxi soirée (22h–6h)', value: '15€/an' },
        { label: 'Pilule contraceptive', value: '60€/an' },
        { label: 'Médicaments hors prescription', value: '30€/an' },
        { label: 'Forfait test de grossesse', value: '10€/an' },
        { label: 'Médecine douce', value: '60€/an' },
        { label: 'Prévention : préservatifs', value: '30€/an' },
        { label: 'Prévention : protections périodiques', value: '60€/an' },
        { label: 'Vaccins non remboursés par la Sécurité sociale', value: '90€/an' },
        { label: 'Consultation psychologue', value: '80€/an' },
      ],
    },
    en: {
      summary: 'Coverage of your optical, dental and hearing costs + your specialist appointments even with excess fees + dedicated student allowances!',
      rows: [
        { label: 'Doctor & pharmacy', value: 'Maximum cover' },
        { label: 'Optical (glasses)', value: 'From €120 to €180 every two years' },
        { label: 'Dental (care, crowns)', value: 'Maximum cover' },
        { label: 'Hospitalisation', value: 'Maximum cover + €15 private room for 30 days' },
        { label: 'Fruit & vegetable allowance', value: '€40/year' },
        { label: 'Evening taxi (10pm–6am)', value: '€15/year' },
        { label: 'Contraceptive pill', value: '€60/year' },
        { label: 'Over-the-counter medication', value: '€30/year' },
        { label: 'Pregnancy test allowance', value: '€10/year' },
        { label: 'Alternative medicine', value: '€60/year' },
        { label: 'Prevention: condoms', value: '€30/year' },
        { label: 'Prevention: period products', value: '€60/year' },
        { label: 'Vaccines not reimbursed by Social Security', value: '€90/year' },
        { label: 'Psychologist consultation', value: '€80/year' },
      ],
    },
  },
}
/** Détail « En savoir + » des offres RC (résumé + tableau de garanties). */
const civilDetails: Record<string, Record<string, PlanDetail>> = {
  security: {
    fr: {
      summary: "L'assurance vie étudiante complète avec une couverture des risques numériques incluse.",
      rows: [
        { label: 'Responsabilité civile (biens personnels / dommages matériels)', value: 'Incluse' },
        { label: 'Accidents corporels', value: 'Inclus' },
        { label: 'Défense et recours (assistance juridique)', value: 'Inclus' },
        { label: 'Assistance', value: 'Incluse' },
        { label: "Internet / e-réputation / usurpation d'identité", value: 'Inclus' },
        { label: 'Dommages aux biens du maître de stage', value: "Jusqu'à 6 mois" },
      ],
    },
    en: {
      summary: 'The complete student life insurance, with digital-risk cover included.',
      rows: [
        { label: 'Personal liability (personal property / material damage)', value: 'Included' },
        { label: 'Personal accidents', value: 'Included' },
        { label: 'Legal defence and recourse (legal assistance)', value: 'Included' },
        { label: 'Assistance', value: 'Included' },
        { label: 'Internet / e-reputation / identity theft', value: 'Included' },
        { label: "Damage to internship host's property", value: 'Up to 6 months' },
      ],
    },
  },
  indispensable: {
    fr: {
      summary: 'La responsabilité civile indispensable à ta vie étudiante.',
      rows: [
        { label: 'Responsabilité civile (biens personnels / dommages matériels)', value: 'Incluse' },
        { label: 'Accidents corporels', value: 'Inclus' },
        { label: 'Défense et recours (assistance juridique)', value: 'Inclus' },
        { label: 'Assistance', value: 'Incluse' },
        { label: "Internet / e-réputation / usurpation d'identité", value: 'Non inclus' },
        { label: 'Dommages aux biens du maître de stage', value: "Jusqu'à 6 mois" },
      ],
    },
    en: {
      summary: 'The personal liability cover essential to your student life.',
      rows: [
        { label: 'Personal liability (personal property / material damage)', value: 'Included' },
        { label: 'Personal accidents', value: 'Included' },
        { label: 'Legal defence and recourse (legal assistance)', value: 'Included' },
        { label: 'Assistance', value: 'Included' },
        { label: 'Internet / e-reputation / identity theft', value: 'Not included' },
        { label: "Damage to internship host's property", value: 'Up to 6 months' },
      ],
    },
  },
}

/** Détail « En savoir + » des packs « étranger » (résumé + tableau de garanties). */
const abroadGuarantees = (locale: 'fr' | 'en') => {
  const fr = [
    { label: 'Durée de couverture', value: "Jusqu'à 24 mois" },
    { label: 'Frais médicaux', value: 'Jusqu’à 350 000€' },
    { label: 'Dentaire', value: 'Limités à 230€' },
    { label: 'Hospitalisation', value: 'Frais réels remboursés à 100% sans franchise et avec avance de frais' },
    { label: 'Rapatriement sanitaire', value: 'Inclus 24h/24, 7j/7' },
    { label: 'Assistance juridique', value: 'Incluse' },
    { label: 'Responsabilité civile privée', value: 'Incluse' },
    { label: 'Assurance locative', value: 'Incluse' },
    { label: 'Assurance bagages', value: 'Incluse' },
    { label: 'Retour anticipé', value: 'Billet retour simple' },
    { label: 'Envoi de médicaments', value: 'Inclus' },
  ]
  const en = [
    { label: 'Cover duration', value: 'Up to 24 months' },
    { label: 'Medical expenses', value: 'Up to €350,000' },
    { label: 'Dental', value: 'Capped at €230' },
    { label: 'Hospitalisation', value: 'Actual costs reimbursed 100% with no excess and upfront payment' },
    { label: 'Medical repatriation', value: 'Included 24/7' },
    { label: 'Legal assistance', value: 'Included' },
    { label: 'Private personal liability', value: 'Included' },
    { label: 'Tenant insurance', value: 'Included' },
    { label: 'Baggage insurance', value: 'Included' },
    { label: 'Early return', value: 'One-way return ticket' },
    { label: 'Medication delivery', value: 'Included' },
  ]
  return locale === 'en' ? en : fr
}
const abroadDetails: Record<string, Record<string, PlanDetail>> = {
  packEurope: {
    fr: {
      summary: "L'assurance internationale qui te couvre pendant tes études, ton Erasmus ou ton stage en Europe.",
      rows: [{ label: 'Zone géographique', value: 'Europe' }, ...abroadGuarantees('fr')],
    },
    en: {
      summary: 'The international cover that protects you during your studies, Erasmus or internship in Europe.',
      rows: [{ label: 'Geographical area', value: 'Europe' }, ...abroadGuarantees('en')],
    },
  },
  packMonde: {
    fr: {
      summary: "L'assurance internationale qui te couvre pendant tes études ou ton stage dans le monde entier.",
      rows: [{ label: 'Zone géographique', value: 'Monde entier' }, ...abroadGuarantees('fr')],
    },
    en: {
      summary: 'The international cover that protects you during your studies or internship anywhere in the world.',
      rows: [{ label: 'Geographical area', value: 'Worldwide' }, ...abroadGuarantees('en')],
    },
  },
}

/** Détail « En savoir + » des packs « j'arrive en France ». */
const franceDetails: Record<string, Record<string, PlanDetail>> = {
  packFranceConfort: {
    fr: {
      summary: 'Une offre pensée pour les étudiants internationaux déjà inscrits à la Sécurité Sociale en France, pour un séjour de + de 3 mois.',
      rows: [
        { label: 'Sécurité sociale', value: 'Non incluse' },
        { label: 'Hospitalisation', value: 'Couverture de base' },
        { label: 'Optique / Dentaire', value: 'Couverture de base' },
        { label: 'Forfait fruits et légumes', value: '30€/an' },
        { label: 'Taxi soirée (22h-6h)', value: '15€/an' },
        { label: 'Pilule contraceptive', value: '40€/an' },
        { label: 'Médicaments hors prescription', value: '20€/an' },
        { label: 'Forfait test de grossesse', value: '10€/an' },
        { label: 'Médecine douce', value: '30€/an' },
        { label: 'Prévention : préservatifs', value: '20€/an' },
        { label: 'Prévention : protections périodiques', value: '30€/an' },
        { label: 'Vaccins non remboursés par la Sécurité sociale', value: '60€/an' },
        { label: 'Consultation psychologue', value: '40€/an' },
        { label: 'Responsabilité civile', value: 'Incluse' },
        { label: 'Assistance / rapatriement', value: 'Inclus' },
      ],
    },
    en: {
      summary: 'A plan for international students already registered with the French Social Security, for a stay of over 3 months.',
      rows: [
        { label: 'Social Security', value: 'Not included' },
        { label: 'Hospitalisation', value: 'Basic cover' },
        { label: 'Optical / Dental', value: 'Basic cover' },
        { label: 'Fruit & vegetable allowance', value: '€30/year' },
        { label: 'Evening taxi (10pm-6am)', value: '€15/year' },
        { label: 'Contraceptive pill', value: '€40/year' },
        { label: 'Over-the-counter medication', value: '€20/year' },
        { label: 'Pregnancy test allowance', value: '€10/year' },
        { label: 'Alternative medicine', value: '€30/year' },
        { label: 'Prevention: condoms', value: '€20/year' },
        { label: 'Prevention: period products', value: '€30/year' },
        { label: 'Vaccines not reimbursed by Social Security', value: '€60/year' },
        { label: 'Psychologist consultation', value: '€40/year' },
        { label: 'Personal liability', value: 'Included' },
        { label: 'Assistance / repatriation', value: 'Included' },
      ],
    },
  },
  packFranceGlobal: {
    fr: {
      summary: 'Une offre pensée pour les étudiants internationaux non-inscrits à la Sécurité Sociale en France, pour un séjour de moins de 3 mois.',
      rows: [
        { label: 'Sécurité sociale', value: 'Incluse' },
        { label: 'Hospitalisation', value: 'Prise en charge jusqu’à 100 % selon niveau de dépenses' },
        { label: 'Soins dentaires', value: '150€/trimestre pour les soins urgents' },
        { label: 'Optique', value: 'Pas de prise en charge' },
        { label: 'Pharmacie, radio et analyses', value: '750€/trimestre max.' },
        { label: 'Responsabilité civile', value: 'Incluse' },
        { label: 'Assistance / rapatriement', value: 'Inclus' },
      ],
    },
    en: {
      summary: 'A plan for international students not registered with the French Social Security, for a stay of under 3 months.',
      rows: [
        { label: 'Social Security', value: 'Included' },
        { label: 'Hospitalisation', value: 'Covered up to 100% depending on expense level' },
        { label: 'Dental care', value: '€150/quarter for emergency care' },
        { label: 'Optical', value: 'Not covered' },
        { label: 'Pharmacy, X-ray and tests', value: '€750/quarter max.' },
        { label: 'Personal liability', value: 'Included' },
        { label: 'Assistance / repatriation', value: 'Included' },
      ],
    },
  },
}

/** Détail « En savoir + » des formules mutuelle Auvergne-Rhône-Alpes. */
const regionalDetails: Record<string, Record<string, PlanDetail>> = {
  primordiale: {
    fr: {
      summary: 'Une couverture santé incluant les soins dentaires et optiques.',
      rows: [
        { label: 'Médecin et pharmacie', value: 'Couverture de base' },
        { label: 'Optique (lunettes)', value: 'De 50 à 200€ tous les deux ans' },
        { label: 'Dentaire (soins, couronnes)', value: 'Couverture de base' },
        { label: 'Hospitalisation', value: 'Couverture de base' },
        { label: 'Pilule contraceptive', value: '40€/an' },
        { label: 'Médicaments hors prescription', value: '20€/an' },
        { label: 'Forfait test de grossesse', value: '10€/an' },
        { label: 'Médecine douce', value: '30€/an' },
        { label: 'Prévention : protections périodiques', value: '30€/an' },
        { label: 'Vaccins non remboursés par la Sécurité sociale', value: '60€/an' },
        { label: 'Consultation psychologue', value: '40€/an' },
        { label: 'Abonnement clubs sportifs', value: '30€/an' },
      ],
    },
    en: {
      summary: 'Health cover including dental and optical care.',
      rows: [
        { label: 'Doctor & pharmacy', value: 'Basic cover' },
        { label: 'Optical (glasses)', value: '€50 to €200 every two years' },
        { label: 'Dental (care, crowns)', value: 'Basic cover' },
        { label: 'Hospitalisation', value: 'Basic cover' },
        { label: 'Contraceptive pill', value: '€40/year' },
        { label: 'Over-the-counter medication', value: '€20/year' },
        { label: 'Pregnancy test allowance', value: '€10/year' },
        { label: 'Alternative medicine', value: '€30/year' },
        { label: 'Prevention: period products', value: '€30/year' },
        { label: 'Vaccines not reimbursed by Social Security', value: '€60/year' },
        { label: 'Psychologist consultation', value: '€40/year' },
        { label: 'Sports club membership', value: '€30/year' },
      ],
    },
  },
  renforcee: {
    fr: {
      summary: "Prise en charge de tes dépenses en optique, dentaire, auditif + tes rdv chez tes spécialistes même avec dépassement d'honoraire + des forfaits dédiés aux étudiants.",
      rows: [
        { label: 'Médecin et pharmacie', value: 'Couverture maximale' },
        { label: 'Optique (lunettes)', value: 'De 120 à 280€ tous les deux ans' },
        { label: 'Dentaire (soins, couronnes)', value: 'Couverture maximale' },
        { label: 'Hospitalisation', value: 'Couverture maximale + 15€ chambre particulière pendant 30 jours' },
        { label: 'Pilule contraceptive', value: '40€/an' },
        { label: 'Médicaments hors prescription', value: '30€/an' },
        { label: 'Forfait test de grossesse', value: '10€/an' },
        { label: 'Médecine douce', value: '60€/an' },
        { label: 'Prévention : protections périodiques', value: '50€/an' },
        { label: 'Vaccins non remboursés par la Sécurité sociale', value: '60€/an' },
        { label: 'Consultation psychologue', value: '60€/an' },
        { label: 'Abonnement clubs sportifs', value: '50€/an' },
      ],
    },
    en: {
      summary: 'Coverage of your optical, dental and hearing costs + specialist appointments even with excess fees + dedicated student allowances.',
      rows: [
        { label: 'Doctor & pharmacy', value: 'Maximum cover' },
        { label: 'Optical (glasses)', value: '€120 to €280 every two years' },
        { label: 'Dental (care, crowns)', value: 'Maximum cover' },
        { label: 'Hospitalisation', value: 'Maximum cover + €15 private room for 30 days' },
        { label: 'Contraceptive pill', value: '€40/year' },
        { label: 'Over-the-counter medication', value: '€30/year' },
        { label: 'Pregnancy test allowance', value: '€10/year' },
        { label: 'Alternative medicine', value: '€60/year' },
        { label: 'Prevention: period products', value: '€50/year' },
        { label: 'Vaccines not reimbursed by Social Security', value: '€60/year' },
        { label: 'Psychologist consultation', value: '€60/year' },
        { label: 'Sports club membership', value: '€50/year' },
      ],
    },
  },
}

/** Guarantee content for a formula, in the current product family. */
function detailContentFor(tierKey: string): PlanDetail | undefined {
  if (isCivil.value) return civilDetails[tierKey]?.[locale.value] ?? civilDetails[tierKey]?.fr
  if (isAbroad.value) return abroadDetails[tierKey]?.[locale.value] ?? abroadDetails[tierKey]?.fr
  if (isArrivingFrance.value) return franceDetails[tierKey]?.[locale.value] ?? franceDetails[tierKey]?.fr
  if (isHealthTopUp.value) return topUpDetail.value
  if (isRegionalAura.value) return regionalDetails[tierKey]?.[locale.value] ?? regionalDetails[tierKey]?.fr
  if (isSolidariteExam.value) return solidariteExamDetail.value
  return planDetails[tierKey]?.[locale.value] ?? planDetails[tierKey]?.fr
}

/**
 * "En savoir +" view-model: the family's guarantee content plus the link to its
 * Calaméo sheet (SI-1455). The sheet is attached here — once, after the family
 * dispatch — because it is keyed by formula slug alone, so every family resolves
 * it the same way and no branch can be forgotten when a product is added.
 */
function detailFor(tierKey: string): ResultDetail | undefined {
  const content = detailContentFor(tierKey)
  if (!content) return undefined
  return { ...content, sheetUrl: guaranteeSheetUrlFor(tierKey) }
}

/** Health results cards (backend formules) for the generic ResultsStep. */
const healthResultCards = computed<ResultCard[]>(() =>
  recommendedProducts.value.map((p) => ({
    key: p.tierKey,
    name: p.bundleKey ? t(`bundles.${p.bundleKey}`) : t(`subscription.results.tiers.${p.tierKey}`),
    description: t(`subscription.results.tierDescriptions.${p.tierKey}`),
    category: t('subscription.results.category'),
    image: productImage,
    imageAlt: t('subscription.results.imageAlt'),
    recommended: p.recommended,
    priceMonth: p.pricePerMonth,
    priceYear: p.pricePerYear,
    detail: detailFor(p.tierKey) ?? null,
  })),
)

/** Civil-liability results cards (backend offers AVE) for the generic ResultsStep:
 *  the two offers returned by `available_bundles`, with the recommended one flagged
 *  by the backend (Sécurité if internet protection, else Indispensable). Annual
 *  cover, so only the per-year price is shown; name/detail/image fall back to the
 *  local copy the API does not carry. */
const civilResultCards = computed<ResultCard[]>(() =>
  recommendedProducts.value.map((p) => ({
    key: p.tierKey,
    name: p.bundleKey ? t(`bundles.${p.bundleKey}`) : detailName(p.tierKey),
    category: t('subscription.civilResults.category'),
    image: civilImages[p.tierKey] ?? '',
    imageAlt: detailName(p.tierKey),
    recommended: p.recommended,
    priceMonth: null,
    priceYear: p.pricePerYear,
    detail: detailFor(p.tierKey) ?? null,
  })),
)

/** Abroad results cards (backend packs PME) for the generic ResultsStep: the two
 *  packs returned by `available_bundles`, with the recommended one flagged by the
 *  backend. Prices come from the bundle (price_international); name/detail/image
 *  fall back to the local copy the API does not carry. */
const abroadResultCards = computed<ResultCard[]>(() =>
  recommendedProducts.value.map((p) => ({
    key: p.tierKey,
    name: p.bundleKey ? t(`bundles.${p.bundleKey}`) : detailName(p.tierKey),
    category: t('subscription.abroadResults.category'),
    image: abroadImages[p.tierKey] ?? '',
    imageAlt: detailName(p.tierKey),
    recommended: p.recommended,
    priceMonth: p.pricePerMonth,
    priceYear: null,
    detail: detailFor(p.tierKey) ?? null,
  })),
)

/** Health top-up results card (backend Tonic-Études, SURCOMPL_SANTE) for the
 *  generic ResultsStep: the single bundle returned by `available_bundles`, flagged
 *  recommended by the backend. Annual cover, so only the per-year price is shown;
 *  name/detail/image fall back to the local copy the API does not carry. */
const healthTopUpResultCards = computed<ResultCard[]>(() =>
  recommendedProducts.value.map((p) => ({
    key: p.tierKey,
    name: p.bundleKey ? t(`bundles.${p.bundleKey}`) : detailName(p.tierKey),
    category: t('subscription.topUp.category'),
    image: topUpImage,
    imageAlt: detailName(p.tierKey),
    recommended: p.recommended,
    priceMonth: null,
    priceYear: p.pricePerYear,
    detail: detailFor(p.tierKey) ?? null,
  })),
)

/** France results card (backend pack PF) for the generic ResultsStep: the single
 *  pack returned by `available_bundles` (recommended one flagged by the backend).
 *  Price comes from the bundle (monthly_price); name/detail/image fall back to the
 *  local copy the API does not carry. */
const franceResultCards = computed<ResultCard[]>(() =>
  recommendedProducts.value.map((p) => ({
    key: p.tierKey,
    name: p.bundleKey ? t(`bundles.${p.bundleKey}`) : detailName(p.tierKey),
    category: t('subscription.franceResults.category'),
    image: franceImages[p.tierKey] ?? '',
    imageAlt: detailName(p.tierKey),
    recommended: p.recommended,
    priceMonth: p.pricePerMonth,
    priceYear: null,
    detail: detailFor(p.tierKey) ?? null,
  })),
)

/** Regional AURA results cards (backend formulas AURA) for the generic ResultsStep:
 *  the two formulas returned by `available_bundles` (Primordiale / Renforcée), with
 *  the recommended one flagged by the backend decision tree (optical + dental). The
 *  monthly rate is shown; name/detail/image fall back to the local copy the API does
 *  not carry. */
const regionalResultCards = computed<ResultCard[]>(() =>
  recommendedProducts.value.map((p) => ({
    key: p.tierKey,
    name: p.bundleKey ? t(`bundles.${p.bundleKey}`) : detailName(p.tierKey),
    category: t('subscription.regionalResults.category'),
    image: regionalImages[p.tierKey] ?? '',
    imageAlt: detailName(p.tierKey),
    recommended: p.recommended,
    priceMonth: p.pricePerMonth,
    priceYear: null,
    detail: detailFor(p.tierKey) ?? null,
  })),
)

/** Solidarité Exam results card (backend bundle SOLIDARITE_EXAM) for the generic
 *  ResultsStep: the single bundle returned by `available_bundles`, flagged
 *  recommended by the backend. Monthly + annual rate shown; name/detail fall back
 *  to the local copy the API does not carry. */
const solidariteExamResultCards = computed<ResultCard[]>(() =>
  recommendedProducts.value.map((p) => ({
    key: p.tierKey,
    name: p.bundleKey ? t(`bundles.${p.bundleKey}`) : detailName(p.tierKey),
    category: t('subscription.examResults.category'),
    image: examImage,
    imageAlt: detailName(p.tierKey),
    recommended: p.recommended,
    priceMonth: p.pricePerMonth,
    priceYear: p.pricePerYear,
    detail: detailFor(p.tierKey) ?? null,
  })),
)

/* --- « En savoir + » : bottom sheet sur mobile --------------------------
 * Sur petit écran le panneau détail (surcomplémentaire Tonic, ou une formule
 * dans le composant ResultsStep) devient une feuille glissante : ancrée en bas,
 * par-dessus un voile, avec le scroll de page verrouillé. Le détail des formules
 * est géré par ResultsStep ; on ne garde ici que le verrou de scroll du détail
 * Tonic. Le seuil (48rem) doit rester cohérent avec la media query de ResultsStep. */
const isMobileSheet = ref(false)
let sheetMql: MediaQueryList | null = null
function onSheetMqlChange(e: MediaQueryListEvent) {
  isMobileSheet.value = e.matches
}

/** Une feuille est ouverte ET on est en mode mobile (→ voile + scroll-lock). */
const sheetOpen = computed(() => isMobileSheet.value && topUpDetailOpen.value)
function closeSheets() {
  topUpDetailOpen.value = false
}
function onSheetKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape') closeSheets()
}

onMounted(() => {
  if (typeof window === 'undefined' || !window.matchMedia) return
  sheetMql = window.matchMedia('(max-width: 48rem)')
  isMobileSheet.value = sheetMql.matches
  sheetMql.addEventListener('change', onSheetMqlChange)
})

// Verrouille le scroll du fond + écoute Échap tant qu'une feuille est ouverte.
watch(sheetOpen, (open) => {
  if (typeof document === 'undefined') return
  document.body.style.overflow = open ? 'hidden' : ''
  if (open) window.addEventListener('keydown', onSheetKeydown)
  else window.removeEventListener('keydown', onSheetKeydown)
})

onBeforeUnmount(() => {
  sheetMql?.removeEventListener('change', onSheetMqlChange)
  if (typeof document !== 'undefined') document.body.style.overflow = ''
  if (typeof window !== 'undefined') window.removeEventListener('keydown', onSheetKeydown)
})

function selectChoice(value: string) {
  if (!props.productKey || !currentStepKey.value) return
  const slug = answerSlug(currentStepKey.value, value, locale.value)
  const next = [...answerPathSlugs.value, slug]
  router.push(subscriptionHref(props.productKey, next, locale.value))
}

/** True when the current step is a decision-tree question (vs a results /
 *  top-up / cross-sell / summary screen). */
const isQuestionStep = computed(
  () => !!currentStepKey.value && !['plan', 'topUp', 'crossSell'].includes(currentStepKey.value),
)

/** Options offered for the current question (AURA narrows optical/dental). */
const currentQuestionOptions = computed<readonly string[]>(() => {
  const k = currentStepKey.value
  if (k === 'opticalExpenses') return opticalOptions.value
  if (k === 'dentalNeeds') return dentalOptions.value
  return k ? stepOptions[k].options : []
})

/** Route a question answer: franceStay may redirect to health, others advance. */
function onQuestionSelect(value: string) {
  if (currentStepKey.value === 'franceStay') selectFranceStay(value)
  else selectChoice(value)
}

function goBack() {
  // Phase « Vos informations » : on délègue à history.back() pour que la
  // navigation in-page et le back navigateur passent par le même chemin
  // (InfoForm met à jour sa sous-étape / son activation en miroir via popstate).
  if (infoActive.value) {
    if (typeof window !== 'undefined') history.back()
    return
  }
  // Chaque étape de question est un router.push, donc l'historique navigateur
  // retrace fidèlement les questions vues — y compris une bascule inter-parcours
  // (arrivee-en-france « > 8 mois » → complémentaire santé), qui converge vers une
  // URL identique à celle du parcours santé natif et ne peut donc pas être
  // reconstruite depuis l'arbre du produit courant. On délègue à history.back()
  // dès qu'une entrée précédente existe ; sinon (accès direct / deep-link) on
  // reconstruit l'étape précédente depuis l'arbre.
  if (typeof window !== 'undefined' && window.history.state?.back) {
    history.back()
    return
  }
  if (!props.productKey) return
  if (answerPathSlugs.value.length > 0) {
    const prev = answerPathSlugs.value.slice(0, -1)
    router.push(subscriptionHref(props.productKey, prev, locale.value))
    return
  }
  router.push(locale.value === DEFAULT_LOCALE ? '/' : `/${locale.value}/`)
}
</script>

<template>
  <section
    class="journey-card"
    :class="{ 'journey-card--exit': showExit }"
    aria-labelledby="journey-question"
  >
    <!-- Écran de sortie (mineur · depenses-sante) — présenté comme une popup
         inline : croix + « changer mon choix » ramènent à l'étape de l'âge. -->
    <div
      v-if="showExit"
      class="journey-exit"
      role="alertdialog"
      aria-labelledby="journey-question"
      aria-describedby="journey-exit-desc"
    >
      <button
        type="button"
        class="journey-exit__close"
        :aria-label="t('subscription.exit.close')"
        @click="goBack"
      >
        <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true">
          <path d="M5 5l10 10M15 5L5 15" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
        </svg>
      </button>

      <span class="journey-exit__badge" aria-hidden="true">
        <svg viewBox="0 0 24 24" width="30" height="30">
          <circle cx="12" cy="12" r="9.5" fill="none" stroke="currentColor" stroke-width="1.8"/>
          <path d="M12 7v6M12 16.5h.01" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/>
        </svg>
      </span>

      <h1 id="journey-question" class="journey-exit__title">{{ t('subscription.exit.title') }}</h1>
      <p id="journey-exit-desc" class="journey-exit__message">
        {{ exitMessage }}
      </p>

      <a class="journey-exit__call" href="tel:+33472767008">
        <span class="journey-exit__call-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="20" height="20">
            <path d="M6.5 3h3l1.5 4-2 1.5a11 11 0 0 0 4.5 4.5l1.5-2 4 1.5v3a2 2 0 0 1-2.2 2A16 16 0 0 1 4.5 5.2 2 2 0 0 1 6.5 3z" fill="currentColor"/>
          </svg>
        </span>
        <span class="journey-exit__call-text">
          <span class="journey-exit__call-title">{{ t('subscription.exit.callTitle') }}</span>
          <span class="journey-exit__call-number">{{ t('subscription.exit.phone') }}</span>
        </span>
      </a>

      <button type="button" class="journey-exit__change" @click="goBack">
        {{ t('subscription.exit.change') }}
      </button>
    </div>

    <template v-else>
    <header class="journey-card__head">
      <div class="journey-card__top-row">
        <button
          type="button"
          class="journey-card__back"
          aria-label="Étape précédente"
          @click="goBack"
        >
          <svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true">
            <path d="M12 5l-5 5 5 5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
        <SubscriptionStepper v-if="infoActive || currentStepKey || showSummary || showCivilSummary || showAbroadSummary || showFranceSummary || showHealthTopUpSummary || showRegionalSummary || showSolidariteExamSummary" :current="stepperCurrent" :total="stepperTotal" />
      </div>
      <p class="journey-card__product">{{ productLabel }}</p>
    </header>

    <template v-if="!infoActive">
    <QuestionStep
      v-if="isQuestionStep && currentStepKey"
      :step-key="currentStepKey"
      :variant="answers.forWhom === 'child' ? 'child' : 'self'"
      :options="currentQuestionOptions"
      :age-label-params="ageLabelParams"
      @select="onQuestionSelect"
    />

    <ResultsStep
      v-else-if="showResults"
      :title="t('subscription.results.title')"
      :cards="healthResultCards"
      :loading="bundlesLoading"
      :error-key="bundlesError"
      :is-mobile-sheet="isMobileSheet"
      @select="selectChoice"
      @retry="reloadBundles"
    />

    <TopUpStep
      v-else-if="showTopUp"
      :name="topUpName"
      :image="topUpImage"
      :price-year="healthTonicPerYear"
      :detail="topUpDetail"
      :is-mobile-sheet="isMobileSheet"
      @choose="selectChoice('subscribe')"
      @skip="selectChoice('skip')"
    />

    <CrossSellStep
      v-else-if="showCrossSell"
      :notice="crossSellNotice"
      :addons="crossSellCards"
      @add="selectChoice"
      @decline="selectChoice('none')"
    />


    <div v-else-if="showSummary" class="journey-summary">
      <h1 id="journey-question" class="journey-summary__title">
        {{ t('subscription.summary.title') }}
      </h1>

      <div class="journey-summary__cards">
        <article
          v-for="(card, i) in summaryCards"
          :key="i"
          class="summary-card"
        >
          <div class="summary-card__media">
            <img
              :src="card.image"
              :alt="card.alt"
              width="800"
              height="446"
              loading="lazy"
            />
          </div>
          <div class="summary-card__body">
            <p class="summary-card__category">{{ card.category }}</p>
            <h2 class="summary-card__name">{{ card.name }}</h2>
            <p v-if="card.pricePerYear !== null" class="summary-card__price">
              <span class="summary-card__amount">{{ formatPrice(card.pricePerYear) }}</span>
              <span class="summary-card__period">{{ t('subscription.results.perYear') }}</span>
            </p>
          </div>
        </article>
      </div>

      <form class="journey-form" novalidate @submit.prevent="submitForm">
        <p class="journey-form__required-legend">
          <span class="journey-form__required" aria-hidden="true">*</span>
          {{ t('common.requiredLegend') }}
        </p>
        <div class="journey-form__field">
          <label class="journey-form__label" for="summary-start-date">
            {{ t('subscription.summary.startDate') }}
            <span class="journey-form__required" :aria-label="t('common.requiredMark')">*</span>
          </label>
          <DateField
            id="summary-start-date"
            v-model="startDate"
            :min="minStartDate"
            :max="startMaxDate"
            :locale="locale"
            :placeholder="t('subscription.summary.datePlaceholder')"
            :aria-label="t('subscription.summary.startDate')"
            :invalid="startError"
            @blur="startTouched = true"
          />
          <p v-if="startError" class="journey-form__error">
            {{ t('subscription.summary.startDateError', { max: startMaxLabel }) }}
          </p>
        </div>

        <div class="journey-form__field" role="radiogroup" :aria-label="t('subscription.summary.duration')">
          <span class="journey-form__label" id="summary-duration-label">
            {{ t('subscription.summary.duration') }}
            <span class="journey-form__required" :aria-label="t('common.requiredMark')">*</span>
          </span>
          <div
            class="journey-form__radios"
            :class="{ 'journey-form__radios--invalid': durationError }"
          >
            <label
              v-for="d in DURATIONS"
              :key="d"
              class="journey-form__radio"
              :class="{ 'journey-form__radio--checked': duration === d }"
            >
              <input
                type="radio"
                name="summary-duration"
                class="journey-form__radio-input"
                :value="d"
                v-model="duration"
              />
              <span class="journey-form__radio-text">
                {{ t('subscription.summary.durationMonths', { count: d }) }}
              </span>
            </label>
          </div>
          <p v-if="durationError" class="journey-form__error">
            {{ t('subscription.summary.durationError') }}
          </p>
          <p v-else-if="endDate" class="journey-form__hint">
            {{ t('subscription.summary.endDate', { date: formatDate(endDate) }) }}
          </p>
        </div>

        <p class="journey-form__notice">{{ t('subscription.summary.tacit') }}</p>

        <button type="submit" class="journey-form__submit">
          {{ t('subscription.summary.submit') }}
        </button>
      </form>
    </div>

    <!-- Résultats « responsabilité civile » (offres AVE) : mêmes cartes génériques
         que la santé/l'étranger, alimentées par le back-end (available_bundles). -->
    <ResultsStep
      v-else-if="showCivilResults"
      :title="t('subscription.results.title')"
      :cards="civilResultCards"
      :loading="bundlesLoading"
      :error-key="bundlesError"
      :is-mobile-sheet="isMobileSheet"
      @select="selectChoice"
      @retry="reloadBundles"
    />

    <!-- Résultats « étranger » (packs PME) : mêmes cartes génériques que la santé,
         alimentées par le back-end (available_bundles) via abroadResultCards. -->
    <ResultsStep
      v-else-if="showAbroadResults"
      :title="t('subscription.results.title')"
      :cards="abroadResultCards"
      :loading="bundlesLoading"
      :error-key="bundlesError"
      :is-mobile-sheet="isMobileSheet"
      @select="selectChoice"
      @retry="reloadBundles"
    />

    <!-- Résultats « j'arrive en France » (pack PF) : mêmes cartes génériques que
         la santé/l'étranger, alimentées par le back-end (available_bundles). -->
    <ResultsStep
      v-else-if="showFranceResults"
      :title="t('subscription.results.title')"
      :cards="franceResultCards"
      :loading="bundlesLoading"
      :error-key="bundlesError"
      :is-mobile-sheet="isMobileSheet"
      @select="selectChoice"
      @retry="reloadBundles"
    />

    <!-- Résultats « je renforce ma santé » (Tonic-Études) : carte générique
         alimentée par le back-end (available_bundles) via healthTopUpResultCards. -->
    <ResultsStep
      v-else-if="showHealthTopUpResults"
      :title="t('subscription.results.title')"
      :cards="healthTopUpResultCards"
      :loading="bundlesLoading"
      :error-key="bundlesError"
      :is-mobile-sheet="isMobileSheet"
      @select="selectChoice"
      @retry="reloadBundles"
    />

    <!-- Résultats « mutuelle Auvergne-Rhône-Alpes » : Primordiale / Renforcée,
         alimentées par le back-end (available_bundles) via regionalResultCards. -->
    <ResultsStep
      v-else-if="showRegionalResults"
      :title="t('subscription.results.title')"
      :cards="regionalResultCards"
      :loading="bundlesLoading"
      :error-key="bundlesError"
      :is-mobile-sheet="isMobileSheet"
      @select="selectChoice"
      @retry="reloadBundles"
    />

    <!-- Résultats « Solidarité Exam » : bundle unique, alimenté par le back-end
         (available_bundles) via solidariteExamResultCards. -->
    <ResultsStep
      v-else-if="showSolidariteExamResults"
      :title="t('subscription.results.title')"
      :cards="solidariteExamResultCards"
      :loading="bundlesLoading"
      :error-key="bundlesError"
      :is-mobile-sheet="isMobileSheet"
      @select="selectChoice"
      @retry="reloadBundles"
    />

    <!-- « Votre offre sur-mesure » (étranger) : récap pack + plage de séjour
         (date range) + durée + pays de destination + Valider. -->
    <div v-else-if="showAbroadSummary" class="journey-summary">
      <h1 id="journey-question" class="journey-summary__title">
        {{ t('subscription.summary.title') }}
      </h1>

      <div v-if="abroadChosen" class="journey-summary__cards">
        <article class="summary-card">
          <div class="summary-card__media">
            <img
              :src="abroadImages[abroadChosen.key]"
              :alt="detailName(abroadChosen.key)"
              width="800"
              height="446"
              loading="lazy"
            />
          </div>
          <div class="summary-card__body">
            <p class="summary-card__category">{{ t('subscription.abroadResults.category') }}</p>
            <h2 class="summary-card__name">{{ detailName(abroadChosen.key) }}</h2>
            <p v-if="abroadChosen.pricePerMonth !== null" class="summary-card__price">
              <span class="summary-card__amount">{{ formatPrice(abroadChosen.pricePerMonth) }}</span>
              <span class="summary-card__period">{{ t('subscription.results.perMonth') }}</span>
            </p>
          </div>
        </article>
      </div>

      <form class="journey-form" novalidate @submit.prevent="submitForm">
        <p class="journey-form__required-legend">
          <span class="journey-form__required" aria-hidden="true">*</span>
          {{ t('common.requiredLegend') }}
        </p>

        <div class="journey-form__field">
          <div class="journey-form__duo">
            <div class="journey-form__subfield">
              <label class="journey-form__label" for="abroad-start">
                {{ t('subscription.summary.stayStart') }}
                <span class="journey-form__required" :aria-label="t('common.requiredMark')">*</span>
              </label>
              <DateField
                id="abroad-start"
                v-model="startDate"
                :min="minStartDate"
                :max="startMaxDate"
                :locale="locale"
                :placeholder="t('subscription.summary.datePlaceholder')"
                :aria-label="t('subscription.summary.stayStart')"
                :invalid="startError"
                @blur="startTouched = true"
              />
            </div>
            <div class="journey-form__subfield">
              <label class="journey-form__label" for="abroad-end">
                {{ t('subscription.summary.stayEnd') }}
                <span class="journey-form__required" :aria-label="t('common.requiredMark')">*</span>
              </label>
              <DateField
                id="abroad-end"
                v-model="stayEnd"
                :min="stayEndMin"
                :max="stayEndMax"
                :disabled="!startDate"
                :locale="locale"
                :placeholder="t('subscription.summary.datePlaceholder')"
                :aria-label="t('subscription.summary.stayEnd')"
                :invalid="stayEndError"
              />
            </div>
          </div>
          <p v-if="startError || stayEndError" class="journey-form__error">
            {{ t('subscription.summary.stayError') }}
          </p>
          <p v-else-if="stayDurationText" class="journey-form__hint">
            {{ t('subscription.summary.stayLength', { duration: stayDurationText }) }}
          </p>
        </div>

        <div class="journey-form__field">
          <label class="journey-form__label" for="abroad-country">
            {{ t('subscription.summary.destinationCountry') }}
            <span class="journey-form__required" :aria-label="t('common.requiredMark')">*</span>
          </label>
          <div
            class="journey-form__select"
            :class="{ 'journey-form__select--invalid': abroadCountryError }"
          >
            <select
              id="abroad-country"
              v-model="abroadCountry"
              class="journey-form__select-el"
            >
              <option value="" disabled>{{ t('subscription.summary.destinationPlaceholder') }}</option>
              <option v-for="c in destinationCountryOptions" :key="c.code" :value="c.code">
                {{ c.flag }} {{ c.name }}
              </option>
            </select>
            <svg class="journey-form__select-chevron" viewBox="0 0 20 20" width="16" height="16" aria-hidden="true">
              <path d="M5 8l5 5 5-5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
          <p v-if="abroadCountryError" class="journey-form__error">
            {{ t('subscription.summary.destinationError') }}
          </p>
        </div>

        <button type="submit" class="journey-form__submit">
          {{ t('subscription.summary.submit') }}
        </button>
      </form>
    </div>

    <!-- « Votre offre sur-mesure » (j'arrive en France) : rappel pack + date de
         début + durée (paliers) + date de fin + nationalité + Valider. -->
    <div v-else-if="showFranceSummary" class="journey-summary">
      <h1 id="journey-question" class="journey-summary__title">
        {{ t('subscription.summary.title') }}
      </h1>

      <div v-if="franceChosen" class="journey-summary__cards">
        <article class="summary-card">
          <div class="summary-card__media">
            <img
              :src="franceImages[franceChosen.key]"
              :alt="detailName(franceChosen.key)"
              width="800"
              height="446"
              loading="lazy"
            />
          </div>
          <div class="summary-card__body">
            <p class="summary-card__category">{{ t('subscription.franceResults.category') }}</p>
            <h2 class="summary-card__name">{{ detailName(franceChosen.key) }}</h2>
            <p v-if="franceChosen.pricePerMonth !== null" class="summary-card__price">
              <span class="summary-card__amount">{{ formatPrice(franceChosen.pricePerMonth) }}</span>
              <span class="summary-card__period">{{ t('subscription.results.perMonth') }}</span>
            </p>
          </div>
        </article>
      </div>

      <form class="journey-form" novalidate @submit.prevent="submitForm">
        <p class="journey-form__required-legend">
          <span class="journey-form__required" aria-hidden="true">*</span>
          {{ t('common.requiredLegend') }}
        </p>

        <div class="journey-form__field">
          <label class="journey-form__label" for="france-start-date">
            {{ t('subscription.summary.startDate') }}
            <span class="journey-form__required" :aria-label="t('common.requiredMark')">*</span>
          </label>
          <DateField
            id="france-start-date"
            v-model="startDate"
            :min="minStartDate"
            :max="startMaxDate"
            :locale="locale"
            :placeholder="t('subscription.summary.datePlaceholder')"
            :aria-label="t('subscription.summary.startDate')"
            :invalid="startError"
            @blur="startTouched = true"
          />
          <p v-if="startError" class="journey-form__error">
            {{ t('subscription.summary.startDateError', { max: startMaxLabel }) }}
          </p>
        </div>

        <div class="journey-form__field" role="radiogroup" :aria-label="t('subscription.summary.duration')">
          <span class="journey-form__label">
            {{ t('subscription.summary.duration') }}
            <span class="journey-form__required" :aria-label="t('common.requiredMark')">*</span>
          </span>
          <div
            class="journey-form__radios"
            :class="{ 'journey-form__radios--invalid': franceDurationError }"
          >
            <label
              v-for="d in franceDurations"
              :key="d"
              class="journey-form__radio"
              :class="{ 'journey-form__radio--checked': duration === d }"
            >
              <input
                type="radio"
                name="france-duration"
                class="journey-form__radio-input"
                :value="d"
                v-model="duration"
              />
              <span class="journey-form__radio-text">
                {{ t('subscription.summary.durationMonths', { count: d }) }}
              </span>
            </label>
          </div>
          <p v-if="franceDurationError" class="journey-form__error">
            {{ t('subscription.summary.durationError') }}
          </p>
          <p v-else-if="startDate && duration && franceDurations.includes(duration) && franceEndDate" class="journey-form__hint">
            {{ t('subscription.summary.endDate', { date: formatDate(franceEndDate) }) }}
          </p>
        </div>

        <div class="journey-form__field">
          <label class="journey-form__label" for="france-nationality">
            {{ t('subscription.summary.nationality') }}
            <span class="journey-form__required" :aria-label="t('common.requiredMark')">*</span>
          </label>
          <div
            class="journey-form__select"
            :class="{ 'journey-form__select--invalid': nationalityError }"
          >
            <select
              id="france-nationality"
              v-model="nationalitySelect"
              class="journey-form__select-el"
            >
              <option value="" disabled>{{ t('subscription.summary.nationalityPlaceholder') }}</option>
              <option v-for="n in nationalities" :key="n.code" :value="n.code">{{ n.label }}</option>
            </select>
            <svg class="journey-form__select-chevron" viewBox="0 0 20 20" width="16" height="16" aria-hidden="true">
              <path d="M5 8l5 5 5-5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
          <p v-if="nationalityError" class="journey-form__error">
            {{ t('subscription.summary.nationalityError') }}
          </p>
        </div>

        <button type="submit" class="journey-form__submit">
          {{ t('subscription.summary.submit') }}
        </button>
      </form>
    </div>

    <!-- « Votre offre sur-mesure » (responsabilité civile) : récap offre +
         date de début (date de fin = +12 mois) + tacite reconduction. -->
    <div v-else-if="showCivilSummary" class="journey-summary">
      <h1 id="journey-question" class="journey-summary__title">
        {{ t('subscription.summary.title') }}
      </h1>

      <div v-if="civilChosen" class="journey-summary__cards">
        <article class="summary-card">
          <div class="summary-card__media">
            <img
              :src="civilImages[civilChosen.key]"
              :alt="detailName(civilChosen.key)"
              width="800"
              height="446"
              loading="lazy"
            />
          </div>
          <div class="summary-card__body">
            <p class="summary-card__category">{{ t('subscription.civilResults.category') }}</p>
            <h2 class="summary-card__name">{{ detailName(civilChosen.key) }}</h2>
            <p v-if="civilChosen.pricePerYear !== null" class="summary-card__price">
              <span class="summary-card__amount">{{ formatPrice(civilChosen.pricePerYear) }}</span>
              <span class="summary-card__period">{{ t('subscription.results.perYear') }}*</span>
            </p>
          </div>
        </article>
      </div>

      <form class="journey-form" novalidate @submit.prevent="submitForm">
        <p class="journey-form__required-legend">
          <span class="journey-form__required" aria-hidden="true">*</span>
          {{ t('common.requiredLegend') }}
        </p>
        <div class="journey-form__field">
          <label class="journey-form__label" for="civil-start-date">
            {{ t('subscription.summary.startDate') }}
            <span class="journey-form__required" :aria-label="t('common.requiredMark')">*</span>
          </label>
          <DateField
            id="civil-start-date"
            v-model="startDate"
            :min="minStartDate"
            :max="startMaxDate"
            :locale="locale"
            :placeholder="t('subscription.summary.datePlaceholder')"
            :aria-label="t('subscription.summary.startDate')"
            :invalid="startError"
            @blur="startTouched = true"
          />
          <p v-if="startError" class="journey-form__error">
            {{ t('subscription.summary.startDateError', { max: startMaxLabel }) }}
          </p>
          <p v-else-if="civilEndDate" class="journey-form__hint">
            {{ t('subscription.summary.endDate', { date: formatDate(civilEndDate) }) }}
          </p>
        </div>

        <p class="journey-form__notice">{{ t('subscription.summary.tacit') }}</p>

        <button type="submit" class="journey-form__submit">
          {{ t('subscription.summary.submit') }}
        </button>
      </form>
    </div>

    <!-- « Votre offre sur-mesure » (renforcer-sante) : encart contextualisé
         (Tonic + assurance) + date de début (durée fixe 1 an) + tacite. -->
    <div v-else-if="showHealthTopUpSummary" class="journey-summary">
      <h1 id="journey-question" class="journey-summary__title">
        {{ t('subscription.summary.title') }}
      </h1>

      <div class="journey-summary__cards">
        <article class="summary-card">
          <div class="summary-card__media">
            <img
              :src="topUpImage"
              :alt="topUpBundleName"
              width="800"
              height="446"
              loading="lazy"
            />
          </div>
          <div class="summary-card__body">
            <p class="summary-card__category">{{ t('subscription.topUp.category') }}</p>
            <h2 class="summary-card__name">{{ topUpBundleName }}</h2>
            <p class="summary-card__price">
              <span class="summary-card__amount">{{ formatPrice(yearlyTotal) }}</span>
              <span class="summary-card__period">{{ t('subscription.results.perYear') }}</span>
            </p>
          </div>
        </article>
      </div>

      <form class="journey-form" novalidate @submit.prevent="submitForm">
        <p class="journey-form__required-legend">
          <span class="journey-form__required" aria-hidden="true">*</span>
          {{ t('common.requiredLegend') }}
        </p>
        <div class="journey-form__field">
          <label class="journey-form__label" for="topup-start-date">
            {{ t('subscription.summary.startDate') }}
            <span class="journey-form__required" :aria-label="t('common.requiredMark')">*</span>
          </label>
          <DateField
            id="topup-start-date"
            v-model="startDate"
            :min="minStartDate"
            :max="startMaxDate"
            :locale="locale"
            :placeholder="t('subscription.summary.datePlaceholder')"
            :aria-label="t('subscription.summary.startDate')"
            :invalid="startError"
            @blur="startTouched = true"
          />
          <p v-if="startError" class="journey-form__error">
            {{ t('subscription.summary.startDateError', { max: startMaxLabel }) }}
          </p>
          <p v-else-if="civilEndDate" class="journey-form__hint">
            {{ t('subscription.summary.endDate', { date: formatDate(civilEndDate) }) }}
          </p>
        </div>

        <p class="journey-form__notice">{{ t('subscription.summary.tacit') }}</p>

        <button type="submit" class="journey-form__submit">
          {{ t('subscription.summary.submit') }}
        </button>
      </form>
    </div>

    <!-- « Votre offre sur-mesure » (mutuelle AURA) : encart contextualisé
         (formule ± assurance) + date de début (durée fixe 1 an) + tacite. -->
    <div v-else-if="showRegionalSummary" class="journey-summary">
      <h1 id="journey-question" class="journey-summary__title">
        {{ t('subscription.summary.title') }}
      </h1>

      <div class="journey-summary__cards">
        <article class="summary-card">
          <div class="summary-card__media">
            <img
              :src="regionalOfferImage"
              :alt="regionalOfferName"
              width="800"
              height="446"
              loading="lazy"
            />
          </div>
          <div class="summary-card__body">
            <p class="summary-card__category">{{ t('subscription.regionalResults.category') }}</p>
            <h2 class="summary-card__name">{{ regionalOfferName }}</h2>
            <p class="summary-card__price">
              <span class="summary-card__amount">{{ formatPrice(yearlyTotal) }}</span>
              <span class="summary-card__period">{{ t('subscription.results.perYear') }}*</span>
            </p>
          </div>
        </article>
      </div>

      <form class="journey-form" novalidate @submit.prevent="submitForm">
        <p class="journey-form__required-legend">
          <span class="journey-form__required" aria-hidden="true">*</span>
          {{ t('common.requiredLegend') }}
        </p>
        <div class="journey-form__field">
          <label class="journey-form__label" for="regional-start-date">
            {{ t('subscription.summary.startDate') }}
            <span class="journey-form__required" :aria-label="t('common.requiredMark')">*</span>
          </label>
          <DateField
            id="regional-start-date"
            v-model="startDate"
            :min="minStartDate"
            :max="startMaxDate"
            :locale="locale"
            :placeholder="t('subscription.summary.datePlaceholder')"
            :aria-label="t('subscription.summary.startDate')"
            :invalid="startError"
            @blur="startTouched = true"
          />
          <p v-if="startError" class="journey-form__error">
            {{ t('subscription.summary.startDateError', { max: startMaxLabel }) }}
          </p>
          <p v-else-if="civilEndDate" class="journey-form__hint">
            {{ t('subscription.summary.endDate', { date: formatDate(civilEndDate) }) }}
          </p>
        </div>

        <p class="journey-form__notice">{{ t('subscription.summary.tacit') }}</p>

        <button type="submit" class="journey-form__submit">
          {{ t('subscription.summary.submit') }}
        </button>
      </form>
    </div>

    <!-- « Votre offre sur-mesure » (Solidarité Exam) : carte offre (bundle unique)
         + date de début (durée fixe 1 an). Couverture à reconduction contractuelle
         (non tacite) : pas de notice de tacite reconduction. -->
    <div v-else-if="showSolidariteExamSummary" class="journey-summary">
      <h1 id="journey-question" class="journey-summary__title">
        {{ t('subscription.summary.title') }}
      </h1>

      <div class="journey-summary__cards">
        <article class="summary-card">
          <div class="summary-card__media">
            <img
              :src="examImage"
              :alt="examOfferName"
              width="800"
              height="446"
              loading="lazy"
            />
          </div>
          <div class="summary-card__body">
            <p class="summary-card__category">{{ t('subscription.examResults.category') }}</p>
            <h2 class="summary-card__name">{{ examOfferName }}</h2>
            <p class="summary-card__price">
              <span class="summary-card__amount">{{ formatPrice(yearlyTotal) }}</span>
              <span class="summary-card__period">{{ t('subscription.results.perYear') }}*</span>
            </p>
          </div>
        </article>
      </div>

      <form class="journey-form" novalidate @submit.prevent="submitForm">
        <p class="journey-form__required-legend">
          <span class="journey-form__required" aria-hidden="true">*</span>
          {{ t('common.requiredLegend') }}
        </p>
        <div class="journey-form__field">
          <label class="journey-form__label" for="exam-start-date">
            {{ t('subscription.summary.startDate') }}
            <span class="journey-form__required" :aria-label="t('common.requiredMark')">*</span>
          </label>
          <DateField
            id="exam-start-date"
            v-model="startDate"
            :min="minStartDate"
            :max="startMaxDate"
            :locale="locale"
            :placeholder="t('subscription.summary.datePlaceholder')"
            :aria-label="t('subscription.summary.startDate')"
            :invalid="startError"
            @blur="startTouched = true"
          />
          <p v-if="startError" class="journey-form__error">
            {{ t('subscription.summary.startDateError', { max: startMaxLabel }) }}
          </p>
          <p v-else-if="civilEndDate" class="journey-form__hint">
            {{ t('subscription.summary.endDate', { date: formatDate(civilEndDate) }) }}
          </p>
        </div>

        <button type="submit" class="journey-form__submit">
          {{ t('subscription.summary.submit') }}
        </button>
      </form>
    </div>

    <div v-else class="journey-card__step">
      <h1 id="journey-question" class="journey-card__question">
        Parcours terminé — suite à construire
      </h1>
      <p class="journey-card__placeholder">
        Choix collectés : <code>{{ JSON.stringify(answers) }}</code>
      </p>
    </div>
    </template>

    <!-- Phase « Vos informations » : composant smart self-contained.
         Toujours monté (hydratation sessionStorage interne) ; rend son
         écran via v-if="active". Gère lui-même son activation (v-model). -->
    <InfoForm
      ref="infoFormRef"
      v-model:active="infoActive"
      v-model:step="infoStep"
      :product-key="productKey"
    />
    </template>
  </section>
</template>

<style scoped>
.journey-card {
  display: flex;
  flex-direction: column;
  gap: 1.75rem;
  background: var(--color-white);
  border-radius: var(--radius-xl);
  padding: clamp(1.5rem, 3vw, 2.25rem);
  border: 1px solid var(--border);
  box-shadow: var(--shadow-sm);
}

.journey-card__head {
  display: flex;
  flex-direction: column;
  gap: 0.875rem;
}
.journey-card__top-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}
.journey-card__back {
  display: grid;
  place-items: center;
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  background: var(--bg-soft);
  color: var(--color-chocolate);
  cursor: pointer;
  flex-shrink: 0;
  transition:
    background var(--dur-fast) var(--ease-out),
    color var(--dur-fast) var(--ease-out),
    transform var(--dur-base) var(--ease-spring);
}
.journey-card__back:hover {
  background: var(--color-peach);
  transform: translateX(-2px);
}
.journey-card__back:focus-visible {
  outline: 2px solid var(--focus-ring);
  outline-offset: 2px;
}
.journey-card__product {
  font-size: var(--text-xs);
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: var(--primary);
}

.journey-card__step {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}
.journey-card__question {
  font-size: var(--text-xl);
  line-height: var(--leading-tight);
}

.journey-card__choices {
  display: grid;
  gap: 0.75rem;
  grid-template-columns: 1fr;
}
@media (min-width: 30rem) {
  .journey-card__choices { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
@media (min-width: 30rem) {
  .journey-card__choices--triple { grid-template-columns: repeat(3, minmax(0, 1fr)); }
}

/* Réponses de l'arbre : lignes radio (pastille + libellé), harmonisées avec
 * les durées. Le clic coche la pastille ET avance aussitôt (navigation URL). */
.choice-tile {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0.6rem;
  padding: 0.875rem 1.125rem;
  background: var(--color-white);
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-md);
  font-size: var(--text-base);
  font-weight: 600;
  color: var(--color-chocolate);
  cursor: pointer;
  text-align: left;
  transition:
    border-color var(--dur-fast) var(--ease-out),
    background var(--dur-fast) var(--ease-out),
    box-shadow var(--dur-fast) var(--ease-out);
}
.choice-tile:hover,
.choice-tile:focus-visible {
  border-color: var(--primary);
  background: color-mix(in oklab, var(--primary) 10%, white);
  color: var(--primary);
}
.choice-tile__label {
  flex: 1 1 auto;
  line-height: 1.3;
}
/* Pastille radio (cercle + point central qui se remplit au survol/focus). */
.choice-tile__radio {
  order: -1;
  flex: 0 0 auto;
  width: 1.15rem;
  height: 1.15rem;
  border: 2px solid var(--border-strong);
  border-radius: 50%;
  background: var(--color-white);
  display: grid;
  place-content: center;
  transition: border-color var(--dur-fast) var(--ease-out);
}
.choice-tile__radio::after {
  content: '';
  width: 0.6rem;
  height: 0.6rem;
  border-radius: 50%;
  background: var(--primary);
  transform: scale(0);
  transition: transform var(--dur-fast) var(--ease-out);
}
.choice-tile:hover .choice-tile__radio,
.choice-tile:focus-visible .choice-tile__radio {
  border-color: var(--primary);
}
.choice-tile:hover .choice-tile__radio::after,
.choice-tile:focus-visible .choice-tile__radio::after {
  transform: scale(1);
}

.journey-card__placeholder {
  color: var(--text-muted);
}
.journey-card__placeholder code {
  background: var(--bg-soft);
  padding: 0.15em 0.4em;
  border-radius: var(--radius-xs);
  font-size: 0.9em;
}

/* ---- Écran surcomplémentaire : Tonic-Études ---- */
.journey-topup {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}
.journey-topup__title {
  font-size: var(--text-xl);
  line-height: var(--leading-tight);
}

.topup-card {
  position: relative;
  display: grid;
  gap: 1.25rem;
  grid-template-columns: 1fr;
  padding: 1.25rem;
  background: color-mix(in oklab, var(--color-peach) 10%, white);
  border: 1px solid var(--primary);
  border-radius: var(--radius-lg);
  box-shadow: 0 0 0 1px var(--primary), var(--shadow-sm);
}
@media (min-width: 38rem) {
  .topup-card {
    grid-template-columns: minmax(0, 18rem) minmax(0, 1fr);
    align-items: center;
  }
}

.topup-card__badge {
  position: absolute;
  top: -0.7rem;
  left: 1.25rem;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.25rem 0.7rem;
  background: var(--primary);
  color: var(--text-on-primary);
  font-size: var(--text-xs);
  font-weight: 700;
  letter-spacing: 0.02em;
  border-radius: var(--radius-pill);
  box-shadow: var(--shadow-sm);
}

.topup-card__media {
  overflow: hidden;
  border-radius: var(--radius-md);
  background: color-mix(in oklab, var(--color-peach) 16%, white);
}
.topup-card__media img {
  display: block;
  width: 100%;
  height: auto;
  aspect-ratio: 800 / 446;
  object-fit: cover;
}

.topup-card__body {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  min-width: 0;
}
.topup-card__category {
  font-size: var(--text-xs);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--text-muted);
}
.topup-card__name {
  font-size: var(--text-lg);
  font-weight: 800;
  color: var(--color-chocolate);
}
.topup-card__price {
  display: flex;
  align-items: baseline;
  gap: 0.1rem;
  padding-top: 0.35rem;
}
.topup-card__amount {
  font-size: var(--text-xl);
  font-weight: 800;
  color: var(--color-chocolate);
}
.topup-card__period {
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--text-muted);
}
.topup-card__actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem 1rem;
  margin-top: 0.85rem;
}
.topup-card__choose {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.6rem 1.5rem;
  background: var(--primary);
  border: 1px solid var(--primary);
  border-radius: var(--radius-md);
  font-size: var(--text-sm);
  font-weight: 700;
  color: var(--text-on-primary);
  cursor: pointer;
  transition:
    background var(--dur-fast) var(--ease-out),
    border-color var(--dur-fast) var(--ease-out),
    transform var(--dur-base) var(--ease-spring);
}
.topup-card__choose:hover {
  background: var(--primary-hover);
  border-color: var(--primary-hover);
  transform: translateY(-1px);
}
.topup-card__choose:focus-visible {
  outline: 2px solid var(--focus-ring);
  outline-offset: 2px;
}
.topup-card__more {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.35rem 0.5rem;
  background: none;
  border: 0;
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--text-muted);
  cursor: pointer;
  transition: color var(--dur-fast) var(--ease-out);
}
.topup-card__more:hover { color: var(--color-chocolate); }
.topup-card__more:focus-visible {
  outline: 2px solid var(--focus-ring);
  outline-offset: 2px;
  border-radius: var(--radius-xs);
}
.topup-card__chevron {
  flex-shrink: 0;
  transition: transform var(--dur-fast) var(--ease-out);
}
.topup-card__chevron.is-open { transform: rotate(180deg); }

.topup-skip {
  align-self: center;
  padding: 0.5rem 1rem;
  background: none;
  border: 0;
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--text-muted);
  text-decoration: underline;
  text-underline-offset: 0.2em;
  cursor: pointer;
  transition: color var(--dur-fast) var(--ease-out);
}
.topup-skip:hover { color: var(--color-chocolate); }
.topup-skip:focus-visible {
  outline: 2px solid var(--focus-ring);
  outline-offset: 2px;
  border-radius: var(--radius-xs);
}

/* ---- Écran cross-sell : assurance complémentaire ---- */
.journey-crosssell {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}
.journey-crosssell__title {
  font-size: var(--text-xl);
  line-height: var(--leading-tight);
}
.journey-crosssell__grid {
  display: grid;
  gap: 0.75rem;
  grid-template-columns: 1fr;
  align-items: stretch;
}
@media (min-width: 40rem) {
  .journey-crosssell__grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}

.crosssell-card {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1.25rem;
  background: var(--color-white);
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-lg);
  transition:
    transform var(--dur-base) var(--ease-spring),
    box-shadow var(--dur-base) var(--ease-out),
    border-color var(--dur-base) var(--ease-out);
}
.crosssell-card:hover {
  transform: translateY(-3px);
  border-color: var(--primary);
  box-shadow: var(--shadow-md);
}
.crosssell-card__head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;
}
.crosssell-card__name {
  font-size: var(--text-lg);
  font-weight: 800;
  color: var(--color-chocolate);
}
.crosssell-card__price {
  font-size: var(--text-base);
  font-weight: 800;
  color: var(--primary);
  white-space: nowrap;
}
.crosssell-card__features {
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
  margin: 0;
  padding: 0;
  list-style: none;
}
.crosssell-card__feature {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  font-size: var(--text-sm);
  line-height: 1.4;
  color: var(--color-chocolate);
}
.crosssell-card__bullet {
  flex-shrink: 0;
  margin-top: 0.15rem;
  color: var(--primary);
}
.crosssell-card__add {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-top: auto;
  padding: 0.6rem 1rem;
  background: var(--primary);
  border: 1px solid var(--primary);
  border-radius: var(--radius-md);
  font-size: var(--text-sm);
  font-weight: 700;
  color: var(--text-on-primary);
  cursor: pointer;
  transition:
    background var(--dur-fast) var(--ease-out),
    border-color var(--dur-fast) var(--ease-out),
    transform var(--dur-base) var(--ease-spring);
}
.crosssell-card__add:hover {
  background: var(--primary-hover);
  border-color: var(--primary-hover);
  transform: translateY(-1px);
}
.crosssell-card__add:focus-visible {
  outline: 2px solid var(--focus-ring);
  outline-offset: 2px;
}

.crosssell-decline {
  align-self: center;
  padding: 0.6rem 1.5rem;
  background: var(--color-white);
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-md);
  font-size: var(--text-sm);
  font-weight: 700;
  color: var(--color-chocolate);
  cursor: pointer;
  transition:
    border-color var(--dur-base) var(--ease-out),
    background var(--dur-base) var(--ease-out),
    transform var(--dur-base) var(--ease-spring);
}
.crosssell-decline:hover {
  border-color: var(--primary);
  background: color-mix(in oklab, var(--color-peach) 18%, white);
  transform: translateY(-2px);
}
.crosssell-decline:focus-visible {
  outline: 2px solid var(--focus-ring);
  outline-offset: 2px;
}

/* ---- Écran récapitulatif + formulaire ---- */
.journey-summary {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}
.journey-summary__title {
  font-size: var(--text-xl);
  line-height: var(--leading-tight);
}
.journey-summary__cards {
  display: grid;
  gap: 0.75rem;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}
@media (max-width: 34rem) {
  .journey-summary__cards { grid-template-columns: 1fr; }
}
/* Carte unique : on l'empêche de s'étirer sur toute la largeur (image énorme),
   sans la centrer — elle reste alignée à gauche dans la 1re colonne. */
.summary-card:only-child {
  max-width: 22rem;
}

.summary-card {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1rem;
  background: color-mix(in oklab, var(--color-peach) 10%, white);
  border: 1px solid var(--primary);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-sm);
}
.summary-card__media {
  overflow: hidden;
  border-radius: var(--radius-md);
  background: color-mix(in oklab, var(--color-peach) 16%, white);
}
.summary-card__media img {
  display: block;
  width: 100%;
  height: auto;
  aspect-ratio: 800 / 446;
  object-fit: cover;
}
.summary-card__body {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}
.summary-card__category {
  font-size: var(--text-xs);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--text-muted);
}
.summary-card__name {
  font-size: var(--text-lg);
  font-weight: 800;
  color: var(--color-chocolate);
}
.summary-card__price {
  display: flex;
  align-items: baseline;
  gap: 0.1rem;
  padding-top: 0.25rem;
}
.summary-card__amount {
  font-size: var(--text-xl);
  font-weight: 800;
  color: var(--color-chocolate);
}
.summary-card__period {
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--text-muted);
}

/* ---- Formulaire de souscription ---- */
.journey-form {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}
.journey-form__field {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}
/* Deux date pickers côte à côte (séjour étranger : début / fin). */
.journey-form__duo {
  display: grid;
  gap: 0.75rem;
  grid-template-columns: 1fr;
}
@media (min-width: 26rem) {
  .journey-form__duo { grid-template-columns: 1fr 1fr; }
}
.journey-form__subfield {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  min-width: 0;
}
.journey-form__label {
  font-size: var(--text-sm);
  font-weight: 700;
  color: var(--color-chocolate);
}
.journey-form__required {
  color: var(--danger);
  font-weight: 700;
  margin-left: 0.15em;
}
.journey-form__required-legend {
  display: flex;
  align-items: baseline;
  gap: 0.35em;
  font-size: var(--text-xs);
  color: var(--text-muted);
}
.journey-form__required-legend .journey-form__required {
  margin-left: 0;
}
.journey-form__error {
  font-size: var(--text-xs);
  font-weight: 600;
  color: var(--danger);
}
.journey-form__hint {
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--primary);
}

/* Select stylé à la charte (chevron custom). */
.journey-form__select {
  position: relative;
  display: flex;
  align-items: center;
}
.journey-form__select-el {
  appearance: none;
  -webkit-appearance: none;
  width: 100%;
  padding: 0.7rem 2.5rem 0.7rem 0.9rem;
  background: var(--color-white);
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-md);
  font: inherit;
  font-size: var(--text-base);
  color: var(--color-chocolate);
  cursor: pointer;
  transition: border-color var(--dur-fast) var(--ease-out), box-shadow var(--dur-fast) var(--ease-out);
}
.journey-form__select-el:focus {
  outline: none;
  border-color: var(--primary);
  box-shadow: 0 0 0 3px var(--focus-ring);
}
.journey-form__select--invalid .journey-form__select-el {
  border-color: var(--danger);
}
.journey-form__select-chevron {
  position: absolute;
  right: 0.9rem;
  color: var(--text-muted);
  pointer-events: none;
}

/* Durée : boutons radio segmentés (UX plus directe qu'un select). */
.journey-form__radios {
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
}
.journey-form__radio {
  flex: 1 1 0;
  min-width: 6rem;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0.6rem;
  padding: 0.7rem 0.9rem;
  background: var(--color-white);
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-md);
  font-size: var(--text-base);
  font-weight: 600;
  color: var(--color-chocolate);
  cursor: pointer;
  transition: border-color var(--dur-fast) var(--ease-out),
    box-shadow var(--dur-fast) var(--ease-out),
    background var(--dur-fast) var(--ease-out);
}
.journey-form__radio:hover {
  border-color: var(--primary);
}
.journey-form__radio--checked {
  border-color: var(--primary);
  background: color-mix(in oklab, var(--primary) 10%, white);
  color: var(--primary);
  font-weight: 700;
}
/* Pastille radio visible (cercle + point au centre quand coché). */
.journey-form__radio-input {
  appearance: none;
  -webkit-appearance: none;
  flex: 0 0 auto;
  width: 1.15rem;
  height: 1.15rem;
  margin: 0;
  border: 2px solid var(--border-strong);
  border-radius: 50%;
  background: var(--color-white);
  cursor: pointer;
  display: grid;
  place-content: center;
  transition: border-color var(--dur-fast) var(--ease-out);
}
.journey-form__radio-input::before {
  content: '';
  width: 0.6rem;
  height: 0.6rem;
  border-radius: 50%;
  background: var(--primary);
  transform: scale(0);
  transition: transform var(--dur-fast) var(--ease-out);
}
.journey-form__radio-input:checked {
  border-color: var(--primary);
}
.journey-form__radio-input:checked::before {
  transform: scale(1);
}
.journey-form__radio-input:focus-visible {
  outline: 2px solid var(--primary);
  outline-offset: 2px;
}
.journey-form__radios--invalid .journey-form__radio {
  border-color: var(--danger);
}
.journey-form__radios--invalid .journey-form__radio-input {
  border-color: var(--danger);
}

.journey-form__notice {
  font-size: var(--text-xs);
  line-height: 1.5;
  color: var(--text-muted);
  padding: 0.75rem 0.9rem;
  background: var(--bg-soft);
  border-radius: var(--radius-md);
}

.journey-form__submit {
  align-self: flex-start;
  padding: 0.8rem 2rem;
  background: var(--primary);
  border: 1px solid var(--primary);
  border-radius: var(--radius-md);
  font-size: var(--text-base);
  font-weight: 700;
  color: var(--text-on-primary);
  cursor: pointer;
  transition:
    background var(--dur-fast) var(--ease-out),
    border-color var(--dur-fast) var(--ease-out),
    transform var(--dur-base) var(--ease-spring);
}
.journey-form__submit:hover {
  background: var(--primary-hover);
  border-color: var(--primary-hover);
  transform: translateY(-1px);
}
.journey-form__submit:focus-visible {
  outline: 2px solid var(--focus-ring);
  outline-offset: 2px;
}
@media (max-width: 30rem) {
  .journey-form__submit { align-self: stretch; text-align: center; }
}

/* Input texte stylé à la charte (cohérent avec .journey-form__select-el). */
.journey-form__input {
  width: 100%;
  padding: 0.7rem 0.9rem;
  background: var(--color-white);
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-md);
  font: inherit;
  font-size: var(--text-base);
  color: var(--color-chocolate);
  transition: border-color var(--dur-fast) var(--ease-out), box-shadow var(--dur-fast) var(--ease-out);
}
.journey-form__input::placeholder { color: var(--text-muted); }
.journey-form__input:focus {
  outline: none;
  border-color: var(--primary);
  box-shadow: 0 0 0 3px var(--focus-ring);
}
.journey-form__input--invalid { border-color: var(--danger); }

/* ---- Écran de sortie (style popup, mais rendu inline dans la carte) ---- */
.journey-card--exit {
  align-items: center;
}
.journey-exit {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 1rem;
  width: 100%;
  padding: 0.25rem 0;
}
.journey-exit__close {
  position: absolute;
  top: -0.75rem;
  right: -0.75rem;
  display: grid;
  place-items: center;
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  background: var(--bg-soft);
  color: var(--color-chocolate);
  cursor: pointer;
  transition:
    background var(--dur-fast) var(--ease-out),
    color var(--dur-fast) var(--ease-out);
}
.journey-exit__close:hover {
  background: color-mix(in oklab, var(--color-cherry) 14%, white);
  color: var(--color-cherry);
}
.journey-exit__close:focus-visible {
  outline: 2px solid var(--focus-ring);
  outline-offset: 2px;
}

.journey-exit__badge {
  display: grid;
  place-items: center;
  width: 3.5rem;
  height: 3.5rem;
  border-radius: 50%;
  background: color-mix(in oklab, var(--color-cherry) 12%, white);
  color: var(--color-cherry);
}
.journey-exit__title {
  font-size: var(--text-2xl);
  font-weight: 800;
  letter-spacing: 0.01em;
  color: var(--color-cherry);
}
.journey-exit__message {
  font-size: var(--text-base);
  line-height: var(--leading-normal);
  color: var(--color-chocolate);
  max-width: 34rem;
}

.journey-exit__call {
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  margin-top: 0.5rem;
  padding: 0.75rem 1.25rem;
  background: var(--primary);
  color: var(--text-on-primary);
  border-radius: var(--radius-md);
  text-decoration: none;
  transition:
    transform var(--dur-base) var(--ease-spring),
    box-shadow var(--dur-base) var(--ease-out),
    background var(--dur-base) var(--ease-out);
}
.journey-exit__call:hover {
  transform: translateY(-2px);
  background: var(--primary-hover);
  box-shadow: var(--shadow-md);
}
.journey-exit__call:focus-visible {
  outline: 2px solid var(--focus-ring);
  outline-offset: 3px;
}
.journey-exit__call-icon {
  display: grid;
  place-items: center;
}
.journey-exit__call-text {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  line-height: 1.15;
}
.journey-exit__call-title {
  font-size: var(--text-xs);
  font-weight: 600;
  opacity: 0.92;
}
.journey-exit__call-number {
  font-size: var(--text-lg);
  font-weight: 800;
  letter-spacing: 0.02em;
}

.journey-exit__change {
  background: none;
  border: 0;
  color: var(--text-muted);
  font-size: var(--text-sm);
  font-weight: 600;
  text-decoration: underline;
  text-underline-offset: 0.2em;
  cursor: pointer;
  transition: color var(--dur-fast) var(--ease-out);
}
.journey-exit__change:hover {
  color: var(--color-chocolate);
}
</style>
